import React, { useState, useEffect, useRef, useMemo } from 'react';
import { setDoc, doc, collection, addDoc, updateDoc, serverTimestamp, onSnapshot, getDocs, query, where, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { db, storage, GEMINI_API_KEY } from '../../config/firebase';
import { sendEmail } from '../../utils/helpers';
import LocationSearchInput from '../../components/ui/LocationSearchInput';
import PhoneInput from '../../components/ui/PhoneInput';
import RsvpPage from './RsvpPage';

const PlannerIcons = {
  Share: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>,
  Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
};

const PhotoQRCode = ({ eventId, eventTitle }) => {
    const uploadUrl = `${window.location?.origin || ''}${window.location?.pathname || ''}?guestUpload=${eventId}${eventTitle ? `&eventName=${encodeURIComponent(eventTitle)}` : ''}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(uploadUrl)}&margin=10`;
    const [recentThumbnails, setRecentThumbnails] = useState([]);

    useEffect(() => {
        let unsubscribe;
        const loadThumbnails = async () => {
            try {
                unsubscribe = onSnapshot(collection(db, `events/${eventId}/guest_photos`), (snapshot) => {
                    const fetched = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    fetched.sort((a, b) => (b.uploadedAt?.toMillis() || 0) - (a.uploadedAt?.toMillis() || 0));
                    setRecentThumbnails(fetched.slice(0, 3));
                });
            } catch (err) {
                console.error("Error loading thumbnails:", err);
            }
        };
        if(eventId) loadThumbnails();
        return () => unsubscribe && unsubscribe();
    }, [eventId]);

    return (
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-5 group">
            <div className="w-20 h-20 bg-gray-50 rounded-xl p-1.5 border border-gray-200 flex-shrink-0 shadow-inner group-hover:border-pink-300 transition-colors">
                <img src={qrUrl} alt="Upload QR Code" className="w-full h-full mix-blend-multiply" />
            </div>
            <div className="flex-grow">
                <h4 className="font-bold text-gray-900 mb-1">Photo Drop QR Code</h4>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">Guests scan this to instantly upload photos directly to your event album—no app required!</p>
                <div className="flex items-center gap-2 flex-wrap">
                    {recentThumbnails.length > 0 && (
                        <div className="flex -space-x-2 mr-2">
                            {recentThumbnails.map(photo => (
                                <img key={photo.id} src={photo.url} className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm" alt="Thumbnail" />
                            ))}
                        </div>
                    )}
                    <a 
                        href={qrUrl} 
                        download={`Event_Photos_QR_${eventId}.png`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="text-xs font-bold text-pink-600 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-lg transition shadow-sm inline-block"
                    >
                        Download High-Res QR
                    </a>
                    <a 
                        href={uploadUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="text-xs font-bold text-white bg-pink-600 hover:bg-pink-700 px-4 py-2 rounded-lg transition shadow-sm inline-block"
                    >
                        Open Gallery ↗
                    </a>
                </div>
            </div>
        </div>
    );
};

const ThankYouEmailWorker = ({ eventId, eventDetails, setEventDetails, user, db }) => {
    useEffect(() => {
        const checkAutoThankYous = async () => {
            if (!eventId || !eventDetails || !eventDetails.date) return;
            if (eventDetails.autoThankYou === false || eventDetails.thankYousSent) return;

            const eventDate = new Date(`${eventDetails.date}T${eventDetails.time || '12:00'}`);
            const now = new Date();
            const daysSinceEvent = (now - eventDate) / (1000 * 60 * 60 * 24);

            if (daysSinceEvent >= 2) {
                try {
                    const guestsSnapshot = await getDocs(collection(db, `users/${user.uid}/evites/${eventId}/guests`));
                    const attendingGuests = guestsSnapshot.docs
                        .map(d => ({ id: d.id, ...d.data() }))
                        .filter(g => g.rsvpStatus === 'attending');

                    if (attendingGuests.length > 0) {
                        const promises = attendingGuests.map(guest => {
                            const imageHtml = eventDetails.generatedImage ? `<img src="${eventDetails.generatedImage}" alt="Event Cover" style="width: 100%; height: auto; max-height: 300px; object-fit: cover; display: block;" />` : '';
                            const html = `
                                <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                                    <div style="max-width: 500px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05); border: 1px solid #e5e7eb;">
                                        ${imageHtml}
                                        <div style="background-color: #db2777; padding: 30px 20px; text-align: center;">
                                            <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 800;">Thank You!</h1>
                                        </div>
                                        <div style="padding: 40px 32px; text-align: center; color: #374151;">
                                            <p style="font-size: 16px; line-height: 1.6; margin-top: 0;">We're so glad you could make it to <strong>${eventDetails.title}</strong>!</p>
                                            <p style="font-size: 16px; line-height: 1.6;">We've set up a shared photo album for the event. We'd love to see your favorite moments! Click below to view the album and drop your own photos.</p>
                                            <div style="margin: 32px 0;">
                                                <a href="${window.location?.origin || ''}/?guestUpload=${eventId}${eventDetails.title ? `&eventName=${encodeURIComponent(eventDetails.title)}` : ''}" style="display: inline-block; background-color: #db2777; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 9999px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 12px rgba(219, 39, 119, 0.3);">
                                                    View & Upload Photos
                                                </a>
                                            </div>
                                        </div>
                                        <div style="background-color: #f9fafb; padding: 16px; text-align: center; border-top: 1px solid #f3f4f6;">
                                            <p style="font-size: 12px; color: #9ca3af; margin: 0;">Powered by <strong style="color: #db2777;">Host-it!</strong></p>
                                        </div>
                                    </div>
                                </div>
                            `;
                            
                            const smsText = `Thanks for coming to ${eventDetails.title}! Share photos here: ${window.location?.origin || ''}/?guestUpload=${eventId}`;
                            return sendEmail(guest.email, `Thank you for coming to ${eventDetails.title}!`, html, guest.phone, smsText);
                        });
                        await Promise.all(promises);
                    }

                    const eventRef = doc(db, `users/${user.uid}/evites/${eventId}`);
                    await updateDoc(eventRef, { thankYousSent: true });
                    setEventDetails(prev => ({ ...prev, thankYousSent: true }));
                    console.log("Automated thank yous sent successfully!");
                } catch (err) {
                    console.error("Failed to send automated thank yous:", err);
                }
            }
        };
        if(eventId && user) checkAutoThankYous();
    }, [eventId, eventDetails, setEventDetails, user, db]);

    return null; 
};

function LargePreviewModal({ imageUrl, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[400] p-4 cursor-zoom-out" onClick={onClose}>
            <div className="relative w-full h-full max-w-7xl flex items-center justify-center animate-fade-in" onClick={(e) => e.stopPropagation()}>
                <img 
                    src={imageUrl} 
                    alt="Expanded preview" 
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default" 
                />
                <button
                    onClick={onClose}
                    className="fixed top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full h-10 w-10 flex items-center justify-center text-xl font-bold shadow-lg transition z-50 border border-white/20 cursor-pointer"
                >
                    &times;
                </button>
            </div>
        </div>
    );
}

export default function EViteCreator({ user = { uid: 'mock_user' }, navigateTo = () => {}, eventId: propEventId, isMapsScriptLoaded }) {
    const [step, setStep] = useState(1); 
    const [creationStep, setCreationStep] = useState(1);
    const totalCreationSteps = 4;
    
    const [eventDetails, setEventDetails] = useState({ 
        title: '', 
        date: '', 
        time: '', 
        location: '', 
        locationDetails: '', 
        allowPlusOnes: true, 
        mealOptions: [],
        themeColor: '#ec4899', 
        showGuestList: true, 
        isPotluck: false, 
        potluckItems: [], 
        polls: [], 
        timeline: [], 
        enableGuestbook: true 
    });
    const [potluckItemInput, setPotluckItemInput] = useState(''); 
    const [newPollQuestion, setNewPollQuestion] = useState('');
    const [newPollOptions, setNewPollOptions] = useState('');
    const [timelineTime, setTimelineTime] = useState('');
    const [timelineTitle, setTimelineTitle] = useState('');

    const [pageSettings, setPageSettings] = useState({
        coHosts: '',
        effect: 'confetti', 
        spotifyLink: '',
        customQuestions: [],
        registryLinks: [], 
        goingMessage: '', 
        notGoingMessage: '', 
        allowSongRequests: false 
    });
    const [newQuestion, setNewQuestion] = useState('');
    const [newRegistryTitle, setNewRegistryTitle] = useState('');
    const [newRegistryUrl, setNewRegistryUrl] = useState('');
    const [description, setDescription] = useState('');
    
    const [generatedImageBlob, setGeneratedImageBlob] = useState(null);
    const [generatedImagePreview, setGeneratedImagePreview] = useState('');
    const [generatedText, setGeneratedText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    
    const [playingPreview, setPlayingPreview] = useState(null);
    const audioRef = useRef(null);

    const [history, setHistory] = useState([]);
    const [redoStack, setRedoStack] = useState([]); 

    const saveHistory = () => {
        setHistory(prev => {
            const newState = [...prev, {
                generatedImagePreview,
                generatedText,
                generatedImageBlob,
                customImage,
                mode 
            }];
            return newState.slice(-10); 
        });
        setRedoStack([]); 
    };

    const handleUndo = () => {
        if (history.length === 0) return;
        
        setRedoStack(prev => [...prev, {
            generatedImagePreview,
            generatedText,
            generatedImageBlob,
            customImage,
            mode
        }]);

        const lastState = history[history.length - 1];
        setHistory(prev => prev.slice(0, -1));
        
        if (lastState.mode === 'ai') {
             setGeneratedImagePreview(lastState.generatedImagePreview);
             setGeneratedText(lastState.generatedText);
             setGeneratedImageBlob(lastState.generatedImageBlob);
        } else if (lastState.mode === 'custom') {
             setCustomImage(lastState.customImage);
        }
    };

    const handleRedo = () => {
        if (redoStack.length === 0) return;

        setHistory(prev => [...prev, {
            generatedImagePreview,
            generatedText,
            generatedImageBlob,
            customImage,
            mode
        }]);

        const nextState = redoStack[redoStack.length - 1];
        setRedoStack(prev => prev.slice(0, -1));

        if (nextState.mode === 'ai') {
             setGeneratedImagePreview(nextState.generatedImagePreview);
             setGeneratedText(nextState.generatedText);
             setGeneratedImageBlob(nextState.generatedImageBlob);
        } else if (nextState.mode === 'custom') {
             setCustomImage(nextState.customImage);
        }
    };

    const [mode, setMode] = useState('ai'); 
    const [customImage, setCustomImage] = useState(null);
    const [textBoxes, setTextBoxes] = useState([]);
    const [activeTextId, setActiveTextId] = useState(null);
    const [draggingId, setDraggingId] = useState(null);
    const [isRendering, setIsRendering] = useState(false);
    
    const [editInstruction, setEditInstruction] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [showEditControls, setShowEditControls] = useState(false);
    const [showPreview, setShowPreview] = useState(false); 

    const containerRef = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    const fontOptions = ['Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Brush Script MT'];

    const [guests, setGuests] = useState([]);
    const [newGuestName, setNewGuestName] = useState('');
    const [newGuestEmail, setNewGuestEmail] = useState('');
    const [newGuestPhone, setNewGuestPhone] = useState(''); 
    const [eventId, setEventId] = useState(propEventId || null); 
    const [error, setError] = useState('');
    const [copiedLink, setCopiedLink] = useState(null);
    const [managementLink, setManagementLink] = useState('');

    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastSubject, setBroadcastSubject] = useState('');
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggesting, setIsSuggesting] = useState(false);

    const [playlistCopied, setPlaylistCopied] = useState(false);

    const [toast, setToast] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [previewKey, setPreviewKey] = useState(0);
    const [effectPreviewKey, setEffectPreviewKey] = useState(0);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const togglePreview = (url) => {
        if (audioRef.current) {
            audioRef.current.pause();
            if (playingPreview === url) {
                setPlayingPreview(null);
                audioRef.current = null;
                return;
            }
        }
        if (url) {
            const audio = new Audio(url);
            audio.volume = 0.5;
            audio.play().catch(e => console.error("Audio play failed", e));
            audio.onended = () => setPlayingPreview(null);
            audioRef.current = audio;
            setPlayingPreview(url);
        }
    };

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (propEventId && user && user.uid) {
            setEventId(propEventId);
            setStep(2); 
            
            const docRef = doc(db, `users/${user.uid}/evites/${propEventId}`);
            const unsubscribe = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setEventDetails(prev => ({
                        ...prev,
                        ...data,
                        potluckItems: data.potluckItems || [],
                        polls: data.polls || [], 
                        timeline: data.timeline || [], 
                        enableGuestbook: data.enableGuestbook !== undefined ? data.enableGuestbook : true, 
                        isPotluck: data.isPotluck || false,
                        locationDetails: data.locationDetails || '',
                        themeColor: data.themeColor || '#ec4899',
                        showGuestList: data.showGuestList !== undefined ? data.showGuestList : true
                    }));
                    if (data.pageSettings) {
                        setPageSettings(prev => ({
                            ...prev,
                            ...data.pageSettings
                        }));
                    }
                    setDescription(data.description);
                    setGeneratedImagePreview(data.generatedImage);
                    setGeneratedText(data.generatedText);
                    setManagementLink(`${window.location?.origin || ''}/manage-evite?token=${data.managementToken}`);
                }
            });
            return () => unsubscribe();
        }
    }, [propEventId, user, db]);

    useEffect(() => {
        const moveHandler = (e) => {
            if (!draggingId || !containerRef.current) return;
            
            const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
            const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
            
            if (clientX === undefined || clientY === undefined) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const newXPx = clientX - containerRect.left - dragOffset.current.x;
            const newYPx = clientY - containerRect.top - dragOffset.current.y;
            
            const newX = (newXPx / containerRect.width) * 100;
            const newY = (newYPx / containerRect.height) * 100;
            
            setTextBoxes(prev => prev.map(t => t.id === draggingId ? { ...t, x: newX, y: newY } : t));
        };
        
        const upHandler = () => setDraggingId(null);

        if (draggingId) {
            window.addEventListener('mousemove', moveHandler);
            window.addEventListener('mouseup', upHandler);
            window.addEventListener('touchmove', moveHandler, { passive: false });
            window.addEventListener('touchend', upHandler);
        }
        return () => {
            window.removeEventListener('mousemove', moveHandler);
            window.removeEventListener('mouseup', upHandler);
            window.removeEventListener('touchmove', moveHandler);
            window.removeEventListener('touchend', upHandler);
        };
    }, [draggingId]);

    const handleDragStart = (e, id) => {
        if (e.type === 'mousedown') {
            e.preventDefault();
        }
        e.stopPropagation(); 
        
        if (!containerRef.current) return;
        
        const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
        const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);

        if (clientX === undefined || clientY === undefined) return;

        const box = textBoxes.find(t => t.id === id);
        const containerRect = containerRef.current.getBoundingClientRect();
        
        const currentX = (box.x / 100) * containerRect.width;
        const currentY = (box.y / 100) * containerRect.height;
        
        setDraggingId(id);
        setActiveTextId(id);
        
        dragOffset.current = {
            x: clientX - containerRect.left - currentX,
            y: clientY - containerRect.top - currentY
        };
    };

    useEffect(() => {
        if (!eventId || !user || !user.uid) return;
        const guestsCol = collection(db, `users/${user.uid}/evites/${eventId}/guests`);
        const unsubscribe = onSnapshot(guestsCol, (snapshot) => {
            const guestList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGuests(guestList);
        });
        return () => unsubscribe();
    }, [eventId, user, db]);

    const rsvpSummary = useMemo(() => ({
        attending: guests.reduce((total, guest) => {
            if (guest.status === 'Attending') {
                return total + 1 + (guest.hasPlusOne ? 1 : 0);
            }
            return total;
        }, 0),
        declined: guests.filter(g => g.status === 'Declined').length,
        pending: guests.filter(g => g.status === 'Pending').length,
    }), [guests]);

    const pollResults = useMemo(() => {
        if (!eventDetails.polls) return {};
        const results = {};
        
        eventDetails.polls.forEach(poll => {
            results[poll.id] = {};
            poll.options.forEach(opt => results[poll.id][opt] = 0);
        });

        guests.forEach(guest => {
            if (guest.pollAnswers) {
                Object.keys(guest.pollAnswers).forEach(pollId => {
                    const answer = guest.pollAnswers[pollId];
                    if (results[pollId] && results[pollId][answer] !== undefined) {
                        results[pollId][answer]++;
                    }
                });
            }
        });
        return results;
    }, [eventDetails.polls, guests]);

    if (!user || user.isAnonymous) {
        return (
            <div className="container mx-auto max-w-md py-16 px-4">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center border border-gray-100">
                    <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">Login Required</h2>
                    <p className="text-gray-600 mb-8">Please log in or create an account to design AI invitations and manage your guest lists.</p>
                    <div className="space-y-3">
                        <button onClick={() => navigateTo('login')} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition">Log In</button>
                        <button onClick={() => navigateTo('signup')} className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">Create Account</button>
                    </div>
                </div>
            </div>
        );
    }

    const handleCustomImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCustomImage(URL.createObjectURL(file));
            setShowEditControls(false); 
        }
    };

    const handleAiEdit = async () => {
        if (!customImage || !editInstruction) return;
        saveHistory(); 
        setIsEditing(true);
        setError('');

        try {
            const response = await fetch(customImage);
            const blob = await response.blob();
            
            const base64data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (reader.result) {
                        resolve(reader.result.split(',')[1]); 
                    } else {
                        reject(new Error("Failed to read image data"));
                    }
                };
                reader.onerror = (e) => reject(new Error("File reading failed"));
                reader.readAsDataURL(blob);
            });

            const payload = {
                contents: [{
                    parts: [
                        { text: editInstruction },
                        { inlineData: { mimeType: blob.type || "image/png", data: base64data } }
                    ]
                }],
                generationConfig: { responseModalities: ["IMAGE"] }
            };

            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image-preview:generateContent?key=${GEMINI_API_KEY}`;
            
            const apiRes = await fetch(apiUrl, { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(payload) 
            });
            
            const result = await apiRes.json();

            if (result.error) {
                throw new Error(result.error.message || "Editing model error");
            }

            const newBase64 = result.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;
            
            if (newBase64) {
                setCustomImage(`data:image/png;base64,${newBase64}`);
                setShowEditControls(false);
            } else {
                throw new Error("No image returned from Gemini AI.");
            }

        } catch (e) {
            console.error("AI Edit Error:", e);
            setError(`Failed to update image: ${e.message}`);
        } finally {
            setIsEditing(false);
        }
    };

    const handleAddText = () => {
        if (!customImage) return;
        setTextBoxes([...textBoxes, {
            id: Date.now(),
            text: 'Your Text Here',
            x: 50, y: 50, 
            color: '#000000',
            size: 5, 
            font: 'Arial',
            type: 'text'
        }]);
    };

    const handleAddEmoji = () => {
        if (!customImage) return;
        setTextBoxes([...textBoxes, {
            id: Date.now(),
            text: '🎉', 
            x: 50, y: 50,
            color: '#000000',
            size: 10, 
            font: 'Arial',
            type: 'emoji' 
        }]);
    };

    const handleAddQuestion = () => {
        if (!newQuestion.trim()) return;
        setPageSettings(prev => ({
            ...prev,
            customQuestions: [...prev.customQuestions, newQuestion.trim()]
        }));
        setNewQuestion('');
    };

    const handleRemoveQuestion = (idx) => {
        setPageSettings(prev => ({
            ...prev,
            customQuestions: prev.customQuestions.filter((_, i) => i !== idx)
        }));
    };

    const handleAddRegistryLink = () => {
        if (!newRegistryTitle.trim() || !newRegistryUrl.trim()) return;
        
        let url = newRegistryUrl.trim();
        if (!/^https?:\/\//i.test(url)) {
            url = 'https://' + url;
        }

        setPageSettings(prev => ({
            ...prev,
            registryLinks: [...(prev.registryLinks || []), { title: newRegistryTitle.trim(), url }]
        }));
        setNewRegistryTitle('');
        setNewRegistryUrl('');
    };

    const handleRemoveRegistryLink = (idx) => {
        setPageSettings(prev => ({
            ...prev,
            registryLinks: (prev.registryLinks || []).filter((_, i) => i !== idx)
        }));
    };

    const handleUpdateText = (id, field, value) => {
        setTextBoxes(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
    };

    const handleRemoveText = (id) => {
        setTextBoxes(prev => prev.filter(t => t.id !== id));
        if (activeTextId === id) setActiveTextId(null);
    };

    const handleAddPotluckItem = () => {
        if (!potluckItemInput.trim()) return;
        setEventDetails(prev => ({
            ...prev,
            potluckItems: [...prev.potluckItems, { id: Date.now().toString(), text: potluckItemInput.trim(), assignee: null }]
        }));
        setPotluckItemInput('');
    };

    const handleRemovePotluckItem = (id) => {
        setEventDetails(prev => ({
            ...prev,
            potluckItems: prev.potluckItems.filter(i => i.id !== id)
        }));
    };

    const handleAddTimelineItem = () => {
        if (!timelineTime || !timelineTitle) return;
        setEventDetails(prev => ({
            ...prev,
            timeline: [...prev.timeline, { time: timelineTime, title: timelineTitle }]
        }));
        setTimelineTime('');
        setTimelineTitle('');
    };

    const handleRemoveTimelineItem = (idx) => {
        setEventDetails(prev => ({
            ...prev,
            timeline: prev.timeline.filter((_, i) => i !== idx)
        }));
    };

    const handleAddPoll = () => {
        if (!newPollQuestion.trim() || !newPollOptions.trim()) return;
        const options = newPollOptions.split(',').map(o => o.trim()).filter(Boolean);
        if (options.length < 2) {
            showToast("Please provide at least two options separated by commas.", "error");
            return;
        }
        setEventDetails(prev => ({
            ...prev,
            polls: [...prev.polls, { id: Date.now().toString(), question: newPollQuestion, options }]
        }));
        setNewPollQuestion('');
        setNewPollOptions('');
    };

    const handleRemovePoll = (id) => {
        setEventDetails(prev => ({
            ...prev,
            polls: prev.polls.filter(p => p.id !== id)
        }));
    };

    const generateCustomDesign = async () => {
        if (!customImage) {
            setError("Please upload an image first.");
            return;
        }
        setIsRendering(true);
        setError('');
        
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = "anonymous";
            
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = customImage;
            });

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            textBoxes.forEach(box => {
                const fontSize = (box.size / 100) * canvas.height;
                ctx.font = `bold ${fontSize}px ${box.font}, "Segoe UI Emoji", "Apple Color Emoji"`;
                ctx.fillStyle = box.color;
                ctx.textAlign = 'center'; 
                ctx.textBaseline = 'middle';
                
                const x = (box.x / 100) * canvas.width;
                const y = (box.y / 100) * canvas.height;
                
                ctx.fillText(box.text, x, y);
            });

            canvas.toBlob((blob) => {
                setGeneratedImageBlob(blob);
                setGeneratedImagePreview(URL.createObjectURL(blob));
                setGeneratedText(description || `You're invited to ${eventDetails.title}!`);
                setIsRendering(false);
            }, 'image/png');

        } catch (err) {
            console.error("Custom generation failed:", err);
            setError("Failed to create image. Please try again.");
            setIsRendering(false);
        }
    };

    const handleEventInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEventDetails(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleMealOptionsChange = (e) => {
        const options = e.target.value.split(',').map(opt => opt.trim()).filter(Boolean);
        setEventDetails(prev => ({ ...prev, mealOptions: options }));
    };

    const generateContent = async () => {
        if (!description || !eventDetails.title || !eventDetails.date || !eventDetails.time || !eventDetails.location) {
            setError('Please fill out all fields to generate the e-vite.');
            return;
        }
        if (generatedImagePreview || generatedText) saveHistory(); 
        setIsGenerating(true);
        setError('');

        try {
            const imagePrompt = `A fun, creative party invitation design for an event described as: "${description}". The image should look like a stylish invitation card with a theme matching the description. Do NOT include specific text for date, time, or location on the image.`;
            const imagePayload = { instances: [{ prompt: imagePrompt }], parameters: { "sampleCount": 1 } };
            const imageApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/imagen-4.0-generate-001:predict?key=${GEMINI_API_KEY}`;
            const imageResponse = await fetch(imageApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(imagePayload) });
            const imageResult = await imageResponse.json();
            
            if (imageResult.error) {
                console.error("Gemini Image API Error:", imageResult.error);
                throw new Error(imageResult.error.message || 'Image API returned an error.');
            }

            if (imageResult.predictions?.[0]?.bytesBase64Encoded) {
                const base64 = imageResult.predictions[0].bytesBase64Encoded;
                const imageUrl = `data:image/png;base64,${base64}`;
                setGeneratedImagePreview(imageUrl);
                const blob = await (await fetch(imageUrl)).blob();
                setGeneratedImageBlob(blob);
            } else { throw new Error('Image generation failed: No predictions returned.'); }

            const textPrompt = `Create a short, fun, and exciting invitation for a party. The theme is "${description}". Use this exact information:\n- Title: ${eventDetails.title}\n- Date: ${eventDetails.date}\n- Time: ${eventDetails.time}\n- Location: ${eventDetails.location}\n\nFormat the response like this example, starting with a fun opening line related to the theme:\n\nGet ready for a magical adventure!\n\nYou're invited to:\n[Title]\n\nWhen:\n[Date] at [Time]\n\nWhere:\n[Location]\n\nWe can't wait to celebrate with you!`;
            const textPayload = { contents: [{ role: "user", parts: [{ text: textPrompt }] }] };
            const textApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`;
            const textResponse = await fetch(textApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(textPayload) });
            const textResult = await textResponse.json();
            
            if (textResult.error) {
                 console.error("Gemini Text API Error:", textResult.error);
                 throw new Error(textResult.error.message || 'Text API returned an error.');
            }

            if (textResult.candidates?.[0]) {
                setGeneratedText(textResult.candidates[0].content.parts[0].text);
            } else { throw new Error('Text generation failed.'); }

        } catch (err) {
            console.error("AI Generation Error, using fallback:", err);
            try {
                const fallbackImageUrl = `https://placehold.co/1024x576/f2a2e8/ffffff?text=${encodeURIComponent(eventDetails.title)}`;
                const blob = await(await fetch(fallbackImageUrl)).blob();
                setGeneratedImageBlob(blob);
                setGeneratedImagePreview(fallbackImageUrl);
                
                const fallbackText = `You're invited to:\n${eventDetails.title}\n\nWhen:\n${eventDetails.date} at ${eventDetails.time}\n\nWhere:\n${eventDetails.location}\n\nWe can't wait to see you there!`;
                setGeneratedText(fallbackText);

            } catch (fallbackErr) {
                console.error("Fallback e-vite generation failed:", fallbackErr);
                setError('Something went wrong during generation. Please try again.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleRegenerateImage = async () => {
        saveHistory(); 
        setIsGenerating(true);
        setError('');
        try {
            const imagePrompt = `A fun, creative party invitation design for an event described as: "${description}". The image should look like a stylish invitation card with a theme matching the description. Do NOT include specific text for date, time, or location on the image.`;
            const imagePayload = { instances: [{ prompt: imagePrompt }], parameters: { "sampleCount": 1 } };
            const imageApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/imagen-4.0-generate-001:predict?key=${GEMINI_API_KEY}`;
            const imageResponse = await fetch(imageApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(imagePayload) });
            const imageResult = await imageResponse.json();
            
            if (imageResult.error) {
                console.error("Gemini Image API Error:", imageResult.error);
                throw new Error(imageResult.error.message || 'Image API returned an error.');
            }

            if (imageResult.predictions?.[0]?.bytesBase64Encoded) {
                const base64 = imageResult.predictions[0].bytesBase64Encoded;
                const imageUrl = `data:image/png;base64,${base64}`;
                setGeneratedImagePreview(imageUrl);
                const blob = await (await fetch(imageUrl)).blob();
                setGeneratedImageBlob(blob);
            } else { throw new Error('Image generation failed: No predictions returned.'); }
        } catch (err) {
            console.error("Regenerate Image Error:", err);
            setError('Failed to regenerate image. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleRegenerateText = async () => {
        saveHistory(); 
        setIsGenerating(true);
        setError('');
        try {
            const textPrompt = `Create a short, fun, and exciting invitation for a party. The theme is "${description}". Use this exact information:\n- Title: ${eventDetails.title}\n- Date: ${eventDetails.date}\n- Time: ${eventDetails.time}\n- Location: ${eventDetails.location}\n\nFormat the response like this example, starting with a fun opening line related to the theme:\n\nGet ready for a magical adventure!\n\nYou're invited to:\n[Title]\n\nWhen:\n[Date] at [Time]\n\nWhere:\n[Location]\n\nWe can't wait to celebrate with you!`;
            const textPayload = { contents: [{ role: "user", parts: [{ text: textPrompt }] }] };
            const textApiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`;
            const textResponse = await fetch(textApiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(textPayload) });
            const textResult = await textResponse.json();
            
            if (textResult.error) {
                 console.error("Gemini Text API Error:", textResult.error);
                 throw new Error(textResult.error.message || 'Text API returned an error.');
            }

            if (textResult.candidates?.[0]) {
                setGeneratedText(textResult.candidates[0].content.parts[0].text);
            } else { throw new Error('Text generation failed.'); }
        } catch (err) {
            console.error("Regenerate Text Error:", err);
            setError('Failed to regenerate text. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCreateEvent = async () => {
        let imageUrl = generatedImagePreview; 

        if (!generatedImageBlob && !generatedImagePreview) {
            setError("Please generate or upload an image before saving.");
            return;
        }
        
        setLoading(true); 

        try {
            if (generatedImageBlob) {
                const imagePath = `evites/${user.uid}/${Date.now()}_invite.png`;
                const imageRef = ref(storage, imagePath);
                await uploadBytes(imageRef, generatedImageBlob);
                imageUrl = await getDownloadURL(imageRef);
            }

            const eviteRef = eventId 
                ? doc(db, `users/${user.uid}/evites/${eventId}`)
                : doc(collection(db, `users/${user.uid}/evites`));

            const managementToken = (eventId && eventDetails.managementToken) ? eventDetails.managementToken : `manage_${eviteRef.id}_${Date.now()}`;
            
            const eventData = {
                ...eventDetails,
                description,
                generatedImage: imageUrl,
                generatedText,
                ownerId: user.uid,
                managementToken,
                pageSettings: pageSettings,
                updatedAt: serverTimestamp(),
            };

            if (!eventId) {
                eventData.createdAt = serverTimestamp();
                eventData.status = 'Draft';
            }
            
            await setDoc(eviteRef, eventData, { merge: true });
            
            // Update local state with the saved data, including the uploaded image URL
            setEventDetails(prev => ({
                ...prev,
                ...eventData
            }));
            if (imageUrl) setGeneratedImagePreview(imageUrl);
            setGeneratedImageBlob(null);
            
            setEventId(eviteRef.id);
            setManagementLink(`${window.location?.origin || ''}/manage-evite?token=${managementToken}`);
            setStep(2);
        } catch (err) {
            console.error("Error saving event:", err);
            setError("Failed to save event. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddGuest = async () => {
        if (!newGuestName || (!newGuestEmail && !newGuestPhone)) {
            setError("Please enter a name and either an email or phone number.");
            return;
        }
        setError(''); 

        const guestData = { 
            name: newGuestName, 
            email: newGuestEmail, 
            phone: newGuestPhone,
            status: 'Pending' 
        };

        const guestRef = await addDoc(collection(db, `users/${user.uid}/evites/${eventId}/guests`), guestData);
        const inviteLink = `${window.location?.origin || ''}/#rsvp/${user.uid}_${eventId}_${guestRef.id}`;
        await updateDoc(guestRef, { inviteLink });
        setNewGuestName('');
        setNewGuestEmail('');
        setNewGuestPhone('');
    };

    const handleCopyLink = (link) => {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setCopiedLink(link);
            setTimeout(() => setCopiedLink(null), 2000);
        } catch (err) {
            console.error('Unable to copy', err);
            if (navigator.clipboard) {
                navigator.clipboard.writeText(link).catch(e => console.error(e));
            }
        }
        document.body.removeChild(textArea);
    };

    const handleSendInvites = async () => {
        const targetGuests = guests.filter(g => (g.email || g.phone) && g.status === 'Pending' && !g.inviteSent);
        if (targetGuests.length === 0) {
            showToast("No unsent pending guests found to invite.", "error");
            return;
        }
        
        setConfirmDialog({
            title: "Send Invitations",
            message: `Are you sure you want to send invitations to ${targetGuests.length} guests?`,
            onConfirm: async () => {
                setConfirmDialog(null);
                setSending(true);
                try {
                    const promises = targetGuests.map(async guest => {
                        const hostName = user.plannerName || user.firstName || 'your friend';
                        const inviteNames = pageSettings.coHosts ? pageSettings.coHosts : hostName;
                        const hostLine = `Hosted by ${inviteNames}`;
                        const imageHtml = eventDetails.generatedImage ? `<img src="${eventDetails.generatedImage}" alt="Event Cover" style="width: 100%; height: auto; max-height: 300px; object-fit: cover; display: block;" />` : '';
                        const inviteHtml = `
                            <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                                <div style="max-width: 500px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05); border: 1px solid #e5e7eb;">
                                    ${imageHtml}
                                    <div style="padding: 40px 32px; text-align: center;">
                                        <p style="text-transform: uppercase; letter-spacing: 2px; color: #db2777; font-size: 12px; font-weight: bold; margin: 0 0 12px 0;">You're Invited!</p>
                                        <h1 style="color: #111827; margin: 0 0 8px 0; font-size: 28px; font-weight: 800; line-height: 1.25;">${eventDetails.title}</h1>
                                        <p style="color: #6b7280; font-size: 16px; margin: 0 0 24px 0; font-weight: 500;">${hostLine}</p>
                                        
                                        <div style="background-color: #f9fafb; border-radius: 12px; padding: 20px; margin-bottom: 28px; text-align: left; border: 1px solid #f3f4f6;">
                                            <p style="margin: 0 0 12px 0; font-size: 15px; color: #4b5563; display: flex; align-items: center;">
                                                <span style="font-size: 18px; margin-right: 8px;">📅</span> 
                                                <span><strong>When:</strong><br/>${new Date(eventDetails.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${eventDetails.time}</span>
                                            </p>
                                            <p style="margin: 0; font-size: 15px; color: #4b5563; display: flex; align-items: center; margin-top: 12px;">
                                                <span style="font-size: 18px; margin-right: 8px;">📍</span> 
                                                <span><strong>Where:</strong><br/>${eventDetails.location}</span>
                                            </p>
                                        </div>

                                        <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin-bottom: 32px;">
                                            Hi ${guest.name}, we would absolutely love for you to join us! Please check out the details and let us know if you can make it.
                                        </p>

                                        <a href="${guest.inviteLink}" style="display: inline-block; background-color: #db2777; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 9999px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 12px rgba(219, 39, 119, 0.3);">
                                            Open Invitation & RSVP
                                        </a>
                                        <p style="margin-top: 20px; font-size: 11px; color: #9ca3af;">
                                            Having trouble clicking? Copy this link:<br/>${guest.inviteLink}
                                        </p>
                                    </div>
                                    <div style="background-color: #f9fafb; padding: 16px; text-align: center; border-top: 1px solid #f3f4f6;">
                                        <p style="font-size: 12px; color: #9ca3af; margin: 0;">Powered by <strong style="color: #db2777;">Host-it!</strong></p>
                                    </div>
                                </div>
                            </div>
                        `;

                        const smsText = `Hi ${guest.name}, ${inviteNames} invited you to ${eventDetails.title}! RSVP: ${guest.inviteLink}`;

                        await sendEmail(
                            guest.email,
                            `You're invited to ${eventDetails.title}!`,
                            inviteHtml,
                            guest.phone,
                            smsText
                        );
                        const guestRef = doc(db, `users/${user.uid}/evites/${eventId}/guests/${guest.id}`);
                        return updateDoc(guestRef, { inviteSent: true, inviteSentAt: serverTimestamp() });
                    });
                    await Promise.all(promises);
                    await updateDoc(doc(db, `users/${user.uid}/evites/${eventId}`), { status: 'Sent' });
                    showToast("Invitations sent successfully!");
                } catch (err) {
                    console.error("Error sending invitations:", err);
                    showToast("Failed to send invitations. Please try again.", "error");
                } finally {
                    setSending(false);
                }
            }
        });
    };

    const handleSendSingleInvite = async (guest) => {
        if (!guest.email && !guest.phone) {
            showToast("Please add an email or phone number for this guest first.", "error");
            return;
        }
        setSending(true);
        try {
            const hostName = user.plannerName || user.firstName || 'your friend';
            const inviteNames = pageSettings.coHosts ? pageSettings.coHosts : hostName;
            const hostLine = `Hosted by ${inviteNames}`;
            const imageHtml = eventDetails.generatedImage ? `<img src="${eventDetails.generatedImage}" alt="Event Cover" style="width: 100%; height: auto; max-height: 300px; object-fit: cover; display: block;" />` : '';
            const inviteHtml = `
                <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                    <div style="max-width: 500px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05); border: 1px solid #e5e7eb;">
                        ${imageHtml}
                        <div style="padding: 40px 32px; text-align: center;">
                            <p style="text-transform: uppercase; letter-spacing: 2px; color: #db2777; font-size: 12px; font-weight: bold; margin: 0 0 12px 0;">You're Invited!</p>
                            <h1 style="color: #111827; margin: 0 0 8px 0; font-size: 28px; font-weight: 800; line-height: 1.25;">${eventDetails.title}</h1>
                            <p style="color: #6b7280; font-size: 16px; margin: 0 0 24px 0; font-weight: 500;">${hostLine}</p>
                            
                            <div style="background-color: #f9fafb; border-radius: 12px; padding: 20px; margin-bottom: 28px; text-align: left; border: 1px solid #f3f4f6;">
                                <p style="margin: 0 0 12px 0; font-size: 15px; color: #4b5563; display: flex; align-items: center;">
                                    <span style="font-size: 18px; margin-right: 8px;">📅</span> 
                                    <span><strong>When:</strong><br/>${new Date(eventDetails.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${eventDetails.time}</span>
                                </p>
                                <p style="margin: 0; font-size: 15px; color: #4b5563; display: flex; align-items: center; margin-top: 12px;">
                                    <span style="font-size: 18px; margin-right: 8px;">📍</span> 
                                    <span><strong>Where:</strong><br/>${eventDetails.location}</span>
                                </p>
                            </div>

                            <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin-bottom: 32px;">
                                Hi ${guest.name}, we would absolutely love for you to join us! Please check out the details and let us know if you can make it.
                            </p>

                            <a href="${guest.inviteLink}" style="display: inline-block; background-color: #db2777; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 9999px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 12px rgba(219, 39, 119, 0.3);">
                                Open Invitation & RSVP
                            </a>
                            <p style="margin-top: 20px; font-size: 11px; color: #9ca3af;">
                                Having trouble clicking? Copy this link:<br/>${guest.inviteLink}
                            </p>
                        </div>
                        <div style="background-color: #f9fafb; padding: 16px; text-align: center; border-top: 1px solid #f3f4f6;">
                            <p style="font-size: 12px; color: #9ca3af; margin: 0;">Powered by <strong style="color: #db2777;">Host-it!</strong></p>
                        </div>
                    </div>
                </div>
            `;

            const smsText = `Hi ${guest.name}, ${inviteNames} invited you to ${eventDetails.title}! RSVP: ${guest.inviteLink}`;

            await sendEmail(
                guest.email,
                `You're invited to ${eventDetails.title}!`,
                inviteHtml,
                guest.phone,
                smsText
            );
            const guestRef = doc(db, `users/${user.uid}/evites/${eventId}/guests/${guest.id}`);
            await updateDoc(guestRef, { inviteSent: true, inviteSentAt: serverTimestamp() });
            await updateDoc(doc(db, `users/${user.uid}/evites/${eventId}`), { status: 'Sent' });
            showToast(`Invitation sent to ${guest.name}!`);
        } catch (err) {
            console.error("Error sending invitation:", err);
            showToast("Failed to send invitation.", "error");
        } finally {
            setSending(false);
        }
    };

    const handleSendReminders = async () => {
        const pendingGuests = guests.filter(g => g.status === 'Pending' && (g.email || g.phone) && g.inviteSent);
        if (pendingGuests.length === 0) {
            showToast("No pending guests with sent invitations found.", "error");
            return;
        }
        
        setConfirmDialog({
            title: "Send Reminders",
            message: `Are you sure you want to send reminders to ${pendingGuests.length} guests?`,
            onConfirm: async () => {
                setConfirmDialog(null);
                setSending(true);
                try {
                    const promises = pendingGuests.map(async guest => {
                        const imageHtml = eventDetails.generatedImage ? `<img src="${eventDetails.generatedImage}" alt="Event Cover" style="width: 100%; height: auto; max-height: 300px; object-fit: cover; display: block;" />` : '';
                        const reminderHtml = `
                            <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                                <div style="max-width: 500px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05); border: 1px solid #e5e7eb;">
                                    ${imageHtml}
                                    <div style="padding: 40px 32px; text-align: center;">
                                        <p style="text-transform: uppercase; letter-spacing: 2px; color: #db2777; font-size: 12px; font-weight: bold; margin: 0 0 12px 0;">Don't forget to RSVP!</p>
                                        <h1 style="color: #111827; margin: 0 0 24px 0; font-size: 28px; font-weight: 800; line-height: 1.25;">${eventDetails.title}</h1>
                                        
                                        <div style="background-color: #f9fafb; border-radius: 12px; padding: 20px; margin-bottom: 28px; text-align: left; border: 1px solid #f3f4f6;">
                                            <p style="margin: 0 0 12px 0; font-size: 15px; color: #4b5563; display: flex; align-items: center;">
                                                <span style="font-size: 18px; margin-right: 8px;">📅</span> 
                                                <span><strong>When:</strong><br/>${new Date(eventDetails.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${eventDetails.time}</span>
                                            </p>
                                            <p style="margin: 0; font-size: 15px; color: #4b5563; display: flex; align-items: center; margin-top: 12px;">
                                                <span style="font-size: 18px; margin-right: 8px;">📍</span> 
                                                <span><strong>Where:</strong><br/>${eventDetails.location}</span>
                                            </p>
                                        </div>

                                        <p style="font-size: 16px; color: #4b5563; line-height: 1.6; margin-bottom: 32px;">
                                            Hi ${guest.name}, we're finalizing our headcount and would love to know if you can make it!
                                        </p>

                                        <a href="${guest.inviteLink}" style="display: inline-block; background-color: #db2777; color: #ffffff; text-decoration: none; padding: 14px 32px; border-radius: 9999px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 12px rgba(219, 39, 119, 0.3);">
                                            Open Invitation & RSVP
                                        </a>
                                        <p style="margin-top: 20px; font-size: 11px; color: #9ca3af;">
                                            Having trouble clicking? Copy this link:<br/>${guest.inviteLink}
                                        </p>
                                    </div>
                                    <div style="background-color: #f9fafb; padding: 16px; text-align: center; border-top: 1px solid #f3f4f6;">
                                        <p style="font-size: 12px; color: #9ca3af; margin: 0;">Powered by <strong style="color: #db2777;">Host-it!</strong></p>
                                    </div>
                                </div>
                            </div>
                        `;

                        const smsText = `Hi ${guest.name}, reminder to RSVP for ${eventDetails.title}: ${guest.inviteLink}`;

                        return sendEmail(
                            guest.email,
                            `Reminder: RSVP for ${eventDetails.title}`,
                            reminderHtml,
                            guest.phone,
                            smsText
                        );
                    });
                    await Promise.all(promises);
                    showToast("Reminders sent successfully!");
                } catch (err) {
                    console.error("Error sending reminders:", err);
                    showToast("Failed to send reminders. Please try again.", "error");
                } finally {
                    setSending(false);
                }
            }
        });
    };

    const handleBroadcast = async () => {
        if (!broadcastSubject || !broadcastMessage) {
            showToast("Please enter a subject and message.", "error");
            return;
        }
        setSending(true);
        try {
            const targets = guests.filter(g => g.email || g.phone);
            if (targets.length === 0) {
                showToast("No reachable guests found.", "error");
                setSending(false);
                return;
            }
            const promises = targets.map(guest => {
                const htmlBody = `
                    <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">
                        <div style="max-width: 500px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; padding: 32px; border: 1px solid #e5e7eb; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);">
                            <h2 style="color: #111827; margin-top: 0; margin-bottom: 24px; font-size: 22px;">${broadcastSubject}</h2>
                            <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin-top: 0;">Hi ${guest.name},</p>
                            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">${broadcastMessage.replace(/\n/g, '<br/>')}</p>
                            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 32px 0;" />
                            <p style="color: #6b7280; font-size: 14px; margin-bottom: 0;">Best,<br/><strong>${eventDetails.title} Organizers</strong></p>
                        </div>
                    </div>
                `;

                const shortMsg = broadcastMessage.length > 80 ? broadcastMessage.substring(0, 80) + '...' : broadcastMessage;
                const smsText = `Update for ${eventDetails.title}: ${shortMsg}`;

                return sendEmail(
                    guest.email,
                    `${broadcastSubject}`,
                    htmlBody,
                    guest.phone,
                    smsText
                );
            });
            await Promise.all(promises);
            showToast("Message sent to all guests!");
            setShowBroadcastModal(false);
            setBroadcastSubject('');
            setBroadcastMessage('');
        } catch (err) {
            console.error("Error sending broadcast:", err);
            showToast("Failed to send message.", "error");
        } finally {
            setSending(false);
        }
    };

    const handleExportPlaylist = () => {
        const requests = guests.filter(g => g.songRequest || g.songRequestData);
        if (requests.length === 0) {
            showToast("No songs to export.", "error");
            return;
        }

        const textList = requests.map(g => {
            if (g.songRequestData) {
                return `${g.songRequestData.artistName} - ${g.songRequestData.trackName}`;
            }
            return g.songRequest;
        }).join('\n');

        const textArea = document.createElement("textarea");
        textArea.value = textList;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setPlaylistCopied(true);
            setTimeout(() => setPlaylistCopied(false), 3000);
        } catch (err) {
            console.error('Unable to copy', err);
        }
        document.body.removeChild(textArea);
    };

    const handleGenerateSuggestions = async (loadMore = false) => {
        const isLoadMore = typeof loadMore === 'boolean' ? loadMore : false;
        
        setIsSuggesting(true);
        if (!isLoadMore) setSuggestions([]);
        
        try {
            const listingsQuery = query(collection(db, "listings"), limit(100));
            const querySnapshot = await getDocs(listingsQuery);
            let candidates = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (candidates.length === 0) {
                showToast("No listings found in the marketplace to suggest.", "error");
                setIsSuggesting(false);
                return;
            }

            const eventLocationStr = eventDetails.location || "";
            const eventCity = eventLocationStr.split(',')[0].trim().toLowerCase();
            
            if (eventCity) {
                candidates.sort((a, b) => {
                    const locA = (a.location || "").toLowerCase();
                    const locB = (b.location || "").toLowerCase();
                    const matchA = locA.includes(eventCity);
                    const matchB = locB.includes(eventCity);
                    return matchB - matchA; 
                });
            }

            const currentSuggestionIds = isLoadMore ? suggestions.map(s => s.id) : [];
            const availableCandidates = candidates.filter(c => !currentSuggestionIds.includes(c.id));

            if (availableCandidates.length === 0) {
                if (isLoadMore) showToast("No additional matching items found.", "error");
                setIsSuggesting(false);
                return;
            }

            const inventoryContext = availableCandidates.slice(0, 60).map(c => 
                `ID: ${c.id} | Name: ${c.title} | Category: ${c.category} | Location: ${c.location} | Desc: ${c.description?.substring(0, 100)}`
            ).join('\n');

            const prompt = `I am planning a party.
            Title: "${eventDetails.title}"
            Description: "${description}"
            Event Location: "${eventDetails.location}"
            
            Here is a list of available rental items in our inventory:
            ${inventoryContext}
            
            Task: Select the top 3-4 specific items from this list that would be best for this event based on the theme/needs AND proximity to the event location.
            
            CRITICAL: Prioritize items that are within a ~30 mile radius of the Event Location. If an item is in the same city or a neighboring city, favor it over a better thematic match that is far away.
            
            Format the response as a JSON array of objects with keys: 
            - "id": The exact ID from the list.
            - "reason": A short, fun 1-sentence explanation of why it fits (e.g. "Perfect match and just 5 miles away!").
            Do not include markdown formatting.`;

            const payload = {
                contents: [{ role: "user", parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: "application/json" }
            };
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            const result = await response.json();
            if (result.candidates?.[0]) {
                const text = result.candidates[0].content.parts[0].text;
                try {
                    const jsonStr = text.replace(/```json/gi, '').replace(/```/g, '').trim();
                    const newItems = JSON.parse(jsonStr);
                    
                    const enrichedItems = newItems.map(item => {
                        const fullListing = availableCandidates.find(c => c.id === item.id);
                        return fullListing ? { ...fullListing, suggestionReason: item.reason } : null;
                    }).filter(Boolean);
                    
                    setSuggestions(prev => isLoadMore ? [...prev, ...enrichedItems] : enrichedItems);
                } catch (e) {
                    console.error("Failed to parse AI suggestions:", e);
                }
            }
        } catch (err) {
            console.error("AI Suggestion Error:", err);
            showToast("Failed to get suggestions. Please try again.", "error");
        } finally {
            setIsSuggesting(false);
        }
    };

    return (
        <div className="container mx-auto max-w-4xl py-8">
            <h2 className="text-3xl font-bold mb-6 text-center">E-vite Dashboard</h2>
            {step === 1 && (
                <div className="bg-white p-4 md:p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 space-y-6">
                    
                    {/* Wizard Progress Bar */}
                    <div className="mb-8 px-2 sm:px-6">
                        <div className="flex justify-between items-center relative">
                            <div className="absolute left-0 top-5 transform -translate-y-1/2 w-full h-1 bg-gray-100 -z-10 rounded-full"></div>
                            <div className="absolute left-0 top-5 transform -translate-y-1/2 h-1 bg-pink-500 -z-10 rounded-full transition-all duration-500 ease-in-out" style={{ width: `${((creationStep - 1) / (totalCreationSteps - 1)) * 100}%` }}></div>
                            
                            {['Details', 'Design', 'Features', 'Extras'].map((label, idx) => (
                                <div 
                                    key={idx} 
                                    onClick={() => setCreationStep(idx + 1)}
                                    className="flex flex-col items-center gap-2 relative z-10 cursor-pointer hover:opacity-80 transition-opacity"
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${creationStep > idx + 1 ? 'bg-pink-600 text-white shadow-md' : creationStep === idx + 1 ? 'bg-pink-500 text-white shadow-lg ring-4 ring-pink-100 scale-110' : 'bg-white text-gray-400 border-2 border-gray-200'}`}>
                                        {creationStep > idx + 1 ? '✓' : idx + 1}
                                    </div>
                                    <span className={`text-[10px] sm:text-xs font-bold transition-colors uppercase tracking-wider ${creationStep === idx + 1 ? 'text-pink-600' : 'text-gray-400'}`}>{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {creationStep === 1 && (
                        <div className="animate-fade-in space-y-6">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-black text-gray-900">Event Details</h3>
                                <p className="text-gray-500 text-sm mt-1">Let's start with the essential information for your event.</p>
                            </div>
                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Event Title</label>
                                        <input type="text" name="title" placeholder="e.g. Lily's 5th Birthday" value={eventDetails.title} onChange={handleEventInputChange} className="w-full p-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition shadow-sm bg-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Location</label>
                                        <LocationSearchInput 
                                            initialValue={eventDetails.location} 
                                            onPlaceSelected={(val) => setEventDetails(prev => ({ ...prev, location: val }))} 
                                            isFormInput={true} 
                                            isMapsScriptLoaded={isMapsScriptLoaded} 
                                            placeholder="Location / Address"
                                            autocompleteType="address"
                                            className="w-full p-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition shadow-sm bg-white"
                                        />
                                        <input 
                                            type="text" 
                                            name="locationDetails" 
                                            placeholder="Apt, Suite, Unit (Optional)" 
                                            value={eventDetails.locationDetails} 
                                            onChange={handleEventInputChange} 
                                            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none transition bg-white" 
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Date</label>
                                        <input type="date" name="date" value={eventDetails.date} onChange={handleEventInputChange} className="w-full p-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition shadow-sm bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Time</label>
                                        <input type="time" name="time" value={eventDetails.time} onChange={handleEventInputChange} className="w-full p-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition shadow-sm bg-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {creationStep === 2 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-black text-gray-900">Design & Theme</h3>
                                <p className="text-gray-500 text-sm mt-1">Make your invitation look amazing with AI or your own custom design.</p>
                            </div>

                            <div className="flex justify-center">
                                <div className="bg-gray-100 p-1.5 rounded-full inline-flex shadow-inner">
                                    <button onClick={() => setMode('ai')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'ai' ? 'bg-pink-500 text-white shadow-md scale-105' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}>✨ AI Magic</button>
                                    <button onClick={() => setMode('custom')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'custom' ? 'bg-pink-500 text-white shadow-md scale-105' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}>🎨 Custom Design</button>
                                </div>
                            </div>

                            {mode === 'ai' ? (
                                <div className="bg-purple-50/50 p-6 rounded-2xl border border-purple-100">
                                    <label className="block text-lg font-bold text-purple-900 mb-3 flex items-center gap-2"><span>✨</span> Describe Your Party</label>
                                    <textarea 
                                        value={description} 
                                        onChange={(e) => {
                                            setDescription(e.target.value);
                                            e.target.style.height = 'auto';
                                            e.target.style.height = e.target.scrollHeight + 'px';
                                        }} 
                                        placeholder="e.g., A magical unicorn-themed 5th birthday for Lily with rainbows and sparkles" 
                                        className="w-full p-4 border border-purple-200 rounded-xl resize-none overflow-hidden focus:ring-2 focus:ring-purple-400 outline-none shadow-sm bg-white" 
                                        style={{ minHeight: '6rem' }}
                                        rows={1}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition">
                                        <label className="cursor-pointer block">
                                            <span className="text-gray-600 font-semibold block mb-2">{customImage ? 'Change Background Image' : 'Upload Background Image'}</span>
                                            <input type="file" accept="image/*" onChange={handleCustomImageUpload} className="hidden" />
                                            <span className="bg-white border border-gray-300 px-4 py-2 rounded-md text-sm hover:bg-gray-100">Choose File</span>
                                        </label>
                                    </div>

                                    {customImage && (
                                        <div className="bg-purple-50 border border-purple-100 p-4 rounded-lg">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="font-bold text-purple-800 flex items-center gap-2">
                                                    ✨ AI Magic Edit
                                                </h4>
                                                <button 
                                                    onClick={() => setShowEditControls(!showEditControls)} 
                                                    className="text-xs font-semibold text-purple-600 hover:text-purple-800 underline"
                                                >
                                                    {showEditControls ? 'Cancel' : 'Edit Image'}
                                                </button>
                                            </div>
                                            
                                            {showEditControls && (
                                                <div className="animate-fade-in space-y-3">
                                                    <p className="text-xs text-purple-600">Tell the AI how to change your image (e.g., "Make it look like a watercolor painting", "Add balloons").</p>
                                                    <div className="flex gap-2 items-center">
                                                        <input 
                                                            type="text" 
                                                            value={editInstruction} 
                                                            onChange={(e) => setEditInstruction(e.target.value)} 
                                                            placeholder="What should we change?" 
                                                            className="flex-grow p-2 border border-purple-200 rounded-md text-base sm:text-sm"
                                                            disabled={isEditing}
                                                        />
                                                        <button 
                                                            onClick={handleAiEdit} 
                                                            disabled={isEditing || !editInstruction} 
                                                            className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-purple-700 disabled:bg-purple-300 shadow-sm"
                                                        >
                                                            {isEditing ? 'Editing...' : 'Generate'}
                                                        </button>
                                                        <div className="flex bg-gray-100 rounded-lg p-1 border border-gray-200">
                                                            <button 
                                                                onClick={handleUndo} 
                                                                disabled={history.length === 0 || history[history.length - 1].mode !== 'custom'}
                                                                className="p-1.5 rounded-md hover:bg-white hover:text-purple-600 hover:shadow-sm transition disabled:opacity-30 text-gray-500" 
                                                                title="Undo"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                                            </button>
                                                            <div className="w-px bg-gray-300 mx-1"></div>
                                                            <button 
                                                                onClick={handleRedo} 
                                                                disabled={redoStack.length === 0 || redoStack[redoStack.length - 1].mode !== 'custom'}
                                                                className="p-1.5 rounded-md hover:bg-white hover:text-purple-600 hover:shadow-sm transition disabled:opacity-30 text-gray-500" 
                                                                title="Redo"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {customImage && (
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h3 className="font-bold text-lg">Design Editor</h3>
                                                <div className="flex gap-2">
                                                    <button onClick={handleAddText} className="bg-blue-100 text-blue-700 px-3 py-2 rounded-full text-sm font-semibold hover:bg-blue-200">+ Text</button>
                                                    <button onClick={handleAddEmoji} className="bg-yellow-100 text-yellow-700 px-3 py-2 rounded-full text-sm font-semibold hover:bg-yellow-200">😊 Sticker</button>
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-col gap-6">
                                                <div 
                                                    ref={containerRef}
                                                    className="relative w-full bg-gray-200 rounded-lg overflow-hidden shadow-inner select-none touch-none" 
                                                    style={{ minHeight: '300px' }}
                                                    onClick={() => setActiveTextId(null)}
                                                >
                                                    <img src={customImage} alt="Background" className="w-full h-auto block pointer-events-none" />
                                                    {textBoxes.map(box => (
                                                        <div
                                                            key={box.id}
                                                            onClick={(e) => { e.stopPropagation(); setActiveTextId(box.id); }}
                                                            className={`absolute group flex flex-col items-center ${activeTextId === box.id ? 'z-20' : 'z-10'}`}
                                                            style={{
                                                                left: `${box.x}%`,
                                                                top: `${box.y}%`,
                                                                transform: 'translate(-50%, -50%)',
                                                            }}
                                                        >
                                                            {activeTextId === box.id && (
                                                                <div className="absolute -top-10 flex gap-1 bg-white rounded-full shadow-lg px-3 py-1.5 border border-gray-200 z-30 animate-fade-in">
                                                                    <div
                                                                        onMouseDown={(e) => handleDragStart(e, box.id)}
                                                                        onTouchStart={(e) => handleDragStart(e, box.id)}
                                                                        className="p-1 cursor-grab active:cursor-grabbing text-gray-500 hover:text-pink-500 border-r border-gray-200 pr-2"
                                                                        title="Drag to move"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
                                                                    </div>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleRemoveText(box.id); }}
                                                                        className="p-1 cursor-pointer text-gray-500 hover:text-red-500 pl-2"
                                                                        title="Remove"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <div className={`relative inline-grid items-center justify-items-center p-1 border-2 border-dashed rounded transition-colors ${activeTextId === box.id ? 'border-pink-400 bg-pink-50/10' : 'border-transparent hover:border-blue-300'}`}>
                                                                <span 
                                                                    className="invisible col-start-1 row-start-1 whitespace-pre-wrap px-2"
                                                                    style={{
                                                                        fontFamily: box.font,
                                                                        fontSize: `${box.size * (containerRef.current ? containerRef.current.offsetHeight / 100 : 5)}px`,
                                                                        fontWeight: 'bold',
                                                                        maxWidth: '80vw', 
                                                                        minWidth: '1em'
                                                                    }}
                                                                >
                                                                    {box.text || ' '}
                                                                </span>
                                                                
                                                                <textarea
                                                                    value={box.text}
                                                                    onChange={(e) => handleUpdateText(box.id, 'text', e.target.value)}
                                                                    className="col-start-1 row-start-1 w-full h-full bg-transparent border-none outline-none text-center px-2 cursor-text resize-none overflow-hidden"
                                                                    style={{
                                                                        fontFamily: box.font,
                                                                        fontSize: `${box.size * (containerRef.current ? containerRef.current.offsetHeight / 100 : 5)}px`,
                                                                        color: box.color,
                                                                        fontWeight: 'bold',
                                                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                    }}
                                                                    placeholder="Type here..."
                                                                    autoComplete="off"
                                                                    rows={1}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {activeTextId && (
                                                    <div className="w-full bg-gray-50 p-4 rounded-lg border h-fit shadow-sm">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h4 className="font-bold text-sm text-gray-700">Edit {textBoxes.find(t => t.id === activeTextId)?.type === 'emoji' ? 'Sticker' : 'Text'}</h4>
                                                            <button onClick={() => setActiveTextId(null)} className="text-xs text-blue-500 hover:underline">Done</button>
                                                        </div>
                                                        {textBoxes.filter(t => t.id === activeTextId).map(box => (
                                                            <div key={box.id} className="space-y-4">
                                                                {box.type !== 'emoji' && (
                                                                    <div>
                                                                        <label className="text-xs text-gray-500 block mb-1">Font Style</label>
                                                                        <select value={box.font} onChange={(e) => handleUpdateText(box.id, 'font', e.target.value)} className="w-full p-2 border rounded text-sm bg-white">
                                                                            {fontOptions.map(f => <option key={f} value={f}>{f}</option>)}
                                                                        </select>
                                                                    </div>
                                                                )}
                                                                <div className="flex gap-4">
                                                                    {box.type !== 'emoji' && (
                                                                        <div className="flex-1">
                                                                            <label className="text-xs text-gray-500 block mb-1">Color</label>
                                                                            <div className="flex items-center gap-2">
                                                                                <input type="color" value={box.color} onChange={(e) => handleUpdateText(box.id, 'color', e.target.value)} className="w-full h-9 p-1 border rounded cursor-pointer" />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex-1">
                                                                        <label className="text-xs text-gray-500 block mb-1">Size</label>
                                                                        <input type="range" min="1" max="30" value={box.size} onChange={(e) => handleUpdateText(box.id, 'size', Number(e.target.value))} className="w-full h-9 cursor-pointer" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="border-t border-gray-100 pt-8">
                                <h3 className="text-lg font-bold mb-5 flex items-center gap-2"><span>🎨</span> Theme & Styling</h3>
                                <div className="space-y-6 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                    <div>
                                        <label htmlFor="themeColor" className="block text-sm font-bold text-gray-700 mb-2">Theme Accent Color</label>
                                        <div className="flex items-center gap-3">
                                            <input type="color" id="themeColor" name="themeColor" value={eventDetails.themeColor} onChange={handleEventInputChange} className="h-12 w-12 p-1 border border-gray-200 rounded-lg cursor-pointer bg-white shadow-sm" />
                                            <span className="text-sm font-mono text-gray-500 uppercase bg-white px-3 py-1.5 rounded border border-gray-200">{eventDetails.themeColor}</span>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-3">E-vite Theme Style</label>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {[
                                                { id: 'playful', name: 'Playful Light', bg: 'bg-gray-50', card: 'bg-white', font: 'font-sans', text: 'text-gray-900', border: 'border-gray-200' },
                                                { id: 'classic', name: 'Classic Elegance', bg: 'bg-stone-100', card: 'bg-stone-50', font: 'font-serif', text: 'text-stone-900', border: 'border-stone-300' },
                                                { id: 'dark-elegance', name: 'Dark & Moody', bg: 'bg-gray-900', card: 'bg-gray-800', font: 'font-serif', text: 'text-gray-100', border: 'border-gray-600' },
                                                { id: 'rustic', name: 'Rustic Charm', bg: 'bg-[#fefae0]', card: 'bg-[#faedcd]', font: 'font-serif', text: 'text-[#3a4035]', border: 'border-[#d4a373]' },
                                                { id: 'modern', name: 'Modern Minimal', bg: 'bg-gray-200', card: 'bg-white', font: 'font-sans tracking-tight', text: 'text-black', border: 'border-black' },
                                                { id: 'ocean', name: 'Coastal Breeze', bg: 'bg-sky-50', card: 'bg-white', font: 'font-sans', text: 'text-slate-800', border: 'border-sky-200' },
                                                { id: 'vintage', name: 'Vintage Warmth', bg: 'bg-orange-50', card: 'bg-[#fffbf0]', font: 'font-serif', text: 'text-amber-900', border: 'border-amber-200' },
                                                { id: 'neon', name: 'Neon Night', bg: 'bg-black', card: 'bg-gray-900', font: 'font-sans uppercase tracking-widest text-white', text: 'text-fuchsia-400', border: 'border-fuchsia-500' },
                                                { id: 'garden', name: 'Botanical Garden', bg: 'bg-green-50', card: 'bg-white', font: 'font-serif', text: 'text-green-900', border: 'border-green-200' }
                                            ].map(theme => (
                                                <div 
                                                    key={theme.id}
                                                    onClick={() => setEventDetails(prev => ({ ...prev, eviteTheme: theme.id }))}
                                                    className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                                                        (eventDetails.eviteTheme || 'playful') === theme.id 
                                                        ? 'border-pink-500 ring-2 ring-pink-200 shadow-md transform scale-[1.02]' 
                                                        : 'border-transparent hover:border-gray-300 shadow-sm'
                                                    }`}
                                                >
                                                    <div className={`${theme.bg} p-2 h-16 flex items-center justify-center`}>
                                                        <div className={`${theme.card} ${theme.border} border w-full h-full rounded shadow-sm flex flex-col items-center justify-center`}>
                                                            <span className={`${theme.font} ${theme.text} text-[10px] font-bold`}>Aa</span>
                                                            <div className="w-8 h-1 mt-1 rounded-full opacity-20" style={{ backgroundColor: eventDetails.themeColor || '#ec4899' }}></div>
                                                        </div>
                                                    </div>
                                                    <div className="bg-white border-t border-gray-100 p-1.5 text-center">
                                                        <span className="text-[10px] font-bold text-gray-700">{theme.name}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {creationStep === 3 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-black text-gray-900">Interactive Features</h3>
                                <p className="text-gray-500 text-sm mt-1">Enhance your guest experience with these fun modules.</p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition shadow-sm bg-white">
                                    <input type="checkbox" id="allowPlusOnes" name="allowPlusOnes" checked={eventDetails.allowPlusOnes} onChange={handleEventInputChange} className="mt-1 w-5 h-5 text-pink-600 rounded" />
                                    <div>
                                        <p className="font-bold text-gray-800">Allow +1s</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Guests can bring a date or friend.</p>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition shadow-sm bg-white">
                                    <input type="checkbox" id="showGuestList" name="showGuestList" checked={eventDetails.showGuestList} onChange={handleEventInputChange} className="mt-1 w-5 h-5 text-pink-600 rounded" />
                                    <div>
                                        <p className="font-bold text-gray-800">Public Guest List</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Show who is attending the event.</p>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition shadow-sm bg-white">
                                    <input type="checkbox" id="enableGuestbook" name="enableGuestbook" checked={eventDetails.enableGuestbook} onChange={handleEventInputChange} className="mt-1 w-5 h-5 text-pink-600 rounded" />
                                    <div>
                                        <p className="font-bold text-gray-800">Enable Guestbook</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Allow guests to leave public comments.</p>
                                    </div>
                                </label>
                                
                                <div 
                                    className={`group cursor-pointer p-4 rounded-xl border transition-all duration-300 shadow-sm ${
                                        pageSettings.allowSongRequests 
                                        ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-200' 
                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                    }`}
                                    onClick={() => setPageSettings(prev => ({...prev, allowSongRequests: !prev.allowSongRequests}))}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-transform duration-300 ${pageSettings.allowSongRequests ? 'bg-white shadow-sm scale-110' : 'bg-gray-100 grayscale'}`}>🎧</div>
                                            <div>
                                                <p className="font-bold text-gray-800">Song Requests</p>
                                                <p className="text-xs text-gray-500 mt-0.5">Let guests add to the playlist.</p>
                                            </div>
                                        </div>
                                        <div className={`w-10 h-6 rounded-full transition-colors duration-300 flex items-center px-0.5 flex-shrink-0 ${pageSettings.allowSongRequests ? 'bg-purple-500' : 'bg-gray-300'}`}>
                                            <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${pageSettings.allowSongRequests ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
                                <label htmlFor="mealOptions" className="block text-sm font-bold text-gray-800 mb-2">Meal Options (comma-separated)</label>
                                <input type="text" id="mealOptions" name="mealOptions" value={eventDetails.mealOptions?.join(', ') || ''} onChange={handleMealOptionsChange} placeholder="e.g., Chicken, Fish, Vegetarian" className="w-full p-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition bg-white shadow-sm" />
                            </div>

                            <div className="bg-orange-50/50 p-5 rounded-2xl border border-orange-100">
                                <div className="flex items-center mb-3">
                                    <input 
                                        type="checkbox" 
                                        id="isPotluck" 
                                        name="isPotluck" 
                                        checked={eventDetails.isPotluck} 
                                        onChange={handleEventInputChange} 
                                        className="h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isPotluck" className="ml-3 font-bold text-orange-900 text-lg">Enable Potluck Mode 🍲</label>
                                </div>
                                {eventDetails.isPotluck && (
                                    <div className="mt-3 animate-fade-in bg-white p-4 rounded-xl border border-orange-200">
                                        <p className="text-xs text-orange-700 mb-3 font-medium">List items for guests to bring (e.g., "Chips", "Soda", "Dessert"). Guests can check them off when they RSVP.</p>
                                        <div className="flex gap-2 mb-3">
                                            <input 
                                                type="text" 
                                                value={potluckItemInput} 
                                                onChange={(e) => setPotluckItemInput(e.target.value)} 
                                                placeholder="Add Item" 
                                                className="flex-grow p-3 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-400 transition"
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddPotluckItem()}
                                            />
                                            <button onClick={handleAddPotluckItem} className="bg-orange-100 text-orange-800 px-4 py-2 rounded-xl text-xl font-black hover:bg-orange-200 transition shadow-sm">+</button>
                                        </div>
                                        <div className="space-y-2">
                                            {eventDetails.potluckItems?.map(item => (
                                                <div key={item.id} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-100 text-sm">
                                                    <div>
                                                        <span className={item.assignee ? 'line-through text-gray-400' : 'text-gray-800 font-semibold'}>{item.text}</span>
                                                        {item.assignee && (
                                                            <span className="ml-2 text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                                                Brought by {item.assignee}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button onClick={() => handleRemovePotluckItem(item.id)} className="text-gray-400 hover:text-red-500 ml-2 text-lg font-bold transition">×</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {creationStep === 4 && (
                        <div className="animate-fade-in space-y-8">
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-black text-gray-900">Extras & Finalize</h3>
                                <p className="text-gray-500 text-sm mt-1">Add the finishing touches and generate your e-vite!</p>
                            </div>

                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><span>🕒</span> Event Schedule</h3>
                                <p className="text-xs text-gray-500 mb-4">Add a timeline so guests know what to expect (e.g. 5:00 PM Arrival, 6:00 PM Dinner).</p>
                                
                                {eventDetails.timeline?.length > 0 && (
                                    <div className="space-y-2 mb-5">
                                        {eventDetails.timeline.sort((a,b) => a.time.localeCompare(b.time)).map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                                <div className="flex gap-3 items-center">
                                                    <span className="font-mono font-bold text-pink-600 bg-pink-50 px-2.5 py-1 rounded-lg text-sm">
                                                        {item.time}
                                                    </span>
                                                    <span className="text-sm font-semibold">{item.title}</span>
                                                </div>
                                                <button onClick={() => handleRemoveTimelineItem(idx)} className="text-gray-400 hover:text-red-500 text-lg font-bold transition">×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="w-full sm:w-1/3">
                                        <label className="block text-xs font-bold text-gray-600 mb-1.5">Time</label>
                                        <input 
                                            type="time" 
                                            value={timelineTime} 
                                            onChange={(e) => setTimelineTime(e.target.value)} 
                                            className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white"
                                        />
                                    </div>
                                    <div className="w-full sm:flex-grow">
                                        <label className="block text-xs font-bold text-gray-600 mb-1.5">Activity</label>
                                        <input 
                                            type="text" 
                                            placeholder="e.g. Cake Cutting" 
                                            value={timelineTitle} 
                                            onChange={(e) => setTimelineTitle(e.target.value)} 
                                            className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white"
                                        />
                                    </div>
                                    <div className="w-full sm:w-auto flex items-end">
                                        <button onClick={handleAddTimelineItem} className="w-full bg-blue-100 text-blue-700 px-6 py-3 rounded-xl text-sm font-bold hover:bg-blue-200 transition shadow-sm h-[46px]">Add</button>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 space-y-6">
                                <h3 className="text-lg font-bold flex items-center gap-2"><span>🎉</span> Page Vibes & Details</h3>
                                
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Host(s)</label>
                                    <input 
                                        type="text" 
                                        placeholder="e.g. Alice & Bob" 
                                        value={pageSettings.coHosts} 
                                        onChange={(e) => setPageSettings({...pageSettings, coHosts: e.target.value})} 
                                        className="w-full p-3.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <label className="block text-sm font-bold text-gray-700">Welcome Effect</label>
                                            <button 
                                                onClick={() => setEffectPreviewKey(k => k + 1)}
                                                className="text-[10px] text-pink-600 hover:text-pink-700 font-bold bg-pink-50 hover:bg-pink-100 px-2 py-1 rounded-lg border border-pink-100 transition shadow-sm"
                                            >
                                                Replay ↻
                                            </button>
                                        </div>
                                        <select 
                                            value={pageSettings.effect} 
                                            onChange={(e) => {
                                                setPageSettings({...pageSettings, effect: e.target.value});
                                                setEffectPreviewKey(k => k + 1);
                                            }} 
                                            className="w-full p-3.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-pink-500 outline-none"
                                        >
                                            <option value="confetti">🎉 Confetti Blast</option>
                                            <option value="fireworks">🎆 Fireworks</option>
                                            <option value="balloons">🎈 Balloon Release</option>
                                            <option value="stars">✨ Starburst</option>
                                            <option value="snow">❄️ Falling Snow</option>
                                            <option value="none">None</option>
                                        </select>

                                        {/* Effect Preview Box */}
                                        <div className="p-4 bg-gray-900 rounded-xl relative overflow-hidden h-28 shadow-inner flex items-center justify-center border border-gray-800">
                                            <span className="absolute top-2 left-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest z-20">Preview</span>
                                            <div key={effectPreviewKey} className="absolute inset-0 z-10 pointer-events-none">
                                                {pageSettings.effect === 'confetti' && (
                                                    <div className="absolute inset-0 flex justify-center items-start">
                                                         {[...Array(30)].map((_, i) => (
                                                             <div key={i} className="absolute w-1.5 h-3" style={{
                                                                 backgroundColor: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'][i % 5],
                                                                 left: `${Math.random() * 100}%`,
                                                                 top: `-20%`,
                                                                 animation: `fall-preview ${1 + Math.random()}s linear forwards`,
                                                                 animationDelay: `${Math.random() * 0.3}s`,
                                                                 transform: `rotate(${Math.random() * 360}deg)`
                                                             }}></div>
                                                         ))}
                                                    </div>
                                                )}
                                                {pageSettings.effect === 'fireworks' && (
                                                     <div className="absolute inset-0 flex items-center justify-center">
                                                         {[...Array(5)].map((_, i) => (
                                                             <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-yellow-300" style={{
                                                                 left: `${20 + Math.random() * 60}%`,
                                                                 top: `${20 + Math.random() * 60}%`,
                                                                 animation: `explode-preview ${0.8 + Math.random() * 0.5}s ease-out forwards`,
                                                                 animationDelay: `${i * 0.2}s`
                                                             }}></div>
                                                         ))}
                                                     </div>
                                                )}
                                                {pageSettings.effect === 'balloons' && (
                                                     <div className="absolute inset-0 flex items-end justify-center">
                                                         {[...Array(10)].map((_, i) => (
                                                             <div key={i} className="absolute w-4 h-5 rounded-full opacity-90" style={{
                                                                 backgroundColor: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#a855f7'][i % 5],
                                                                 left: `${10 + Math.random() * 80}%`,
                                                                 bottom: `-30%`,
                                                                 animation: `float-up-preview ${1.5 + Math.random()}s ease-in forwards`,
                                                                 animationDelay: `${Math.random() * 0.4}s`
                                                             }}></div>
                                                         ))}
                                                     </div>
                                                )}
                                                {pageSettings.effect === 'stars' && (
                                                     <div className="absolute inset-0 flex items-center justify-center">
                                                         {[...Array(20)].map((_, i) => (
                                                             <div key={i} className="absolute text-yellow-300 text-[10px]" style={{
                                                                 left: `${Math.random() * 100}%`,
                                                                 top: `${Math.random() * 100}%`,
                                                                 animation: `twinkle-preview ${0.5 + Math.random()}s ease-in-out alternate infinite`,
                                                                 animationDelay: `${Math.random()}s`
                                                             }}>✨</div>
                                                         ))}
                                                     </div>
                                                )}
                                                {pageSettings.effect === 'snow' && (
                                                     <div className="absolute inset-0 flex items-start justify-center">
                                                         {[...Array(30)].map((_, i) => (
                                                             <div key={i} className="absolute w-1 h-1 bg-white rounded-full opacity-80" style={{
                                                                 left: `${Math.random() * 100}%`,
                                                                 top: `-10%`,
                                                                 animation: `snow-preview ${1.5 + Math.random()}s linear forwards`,
                                                                 animationDelay: `${Math.random() * 0.8}s`
                                                             }}></div>
                                                         ))}
                                                     </div>
                                                )}
                                                {(!pageSettings.effect || pageSettings.effect === 'none') && (
                                                    <div className="flex items-center justify-center h-full w-full text-gray-500 text-xs font-medium">No Effect</div>
                                                )}
                                            </div>
                                            <style>{`
                                                @keyframes fall-preview {
                                                    0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
                                                    100% { transform: translateY(120px) rotate(360deg); opacity: 0; }
                                                }
                                                @keyframes explode-preview {
                                                    0% { transform: scale(0.5); opacity: 1; box-shadow: 0 0 0 0px rgba(253, 224, 71, 0.8); }
                                                    100% { transform: scale(4); opacity: 0; box-shadow: 0 0 15px 15px rgba(253, 224, 71, 0); }
                                                }
                                                @keyframes float-up-preview {
                                                    0% { transform: translateY(10px); opacity: 1; }
                                                    100% { transform: translateY(-140px); opacity: 0; }
                                                }
                                                @keyframes twinkle-preview {
                                                    0% { transform: scale(0.3); opacity: 0.1; }
                                                    100% { transform: scale(1.2); opacity: 1; }
                                                }
                                                @keyframes snow-preview {
                                                    0% { transform: translateY(-10px) translateX(0); opacity: 0.8; }
                                                    100% { transform: translateY(120px) translateX(15px); opacity: 0; }
                                                }
                                            `}</style>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Spotify Playlist URL</label>
                                        <input 
                                            type="text" 
                                            placeholder="https://open.spotify.com/..." 
                                            value={pageSettings.spotifyLink} 
                                            onChange={(e) => setPageSettings({...pageSettings, spotifyLink: e.target.value})} 
                                            className="w-full p-3.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Envelope Reveal Animation</label>
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                        {[
                                            { id: 'fade', name: 'Fade Away', icon: '🌫️' },
                                            { id: 'slide-up', name: 'Slide Up', icon: '⬆️' },
                                            { id: 'slide-left', name: 'Swipe Left', icon: '⬅️' },
                                            { id: 'zoom-in', name: 'Zoom In', icon: '🔍' },
                                            { id: 'spin-out', name: 'Spin & Shrink', icon: '🌀' }
                                        ].map(style => (
                                            <div 
                                                key={style.id}
                                                onClick={() => {
                                                    setPageSettings({...pageSettings, openStyle: style.id});
                                                    setPreviewKey(k => k + 1);
                                                }}
                                                className={`cursor-pointer flex flex-col items-center justify-center rounded-xl p-4 border-2 transition-all ${
                                                    (pageSettings.openStyle || 'fade') === style.id 
                                                    ? 'border-pink-500 bg-pink-50 shadow-md transform scale-[1.02]' 
                                                    : 'border-gray-200 bg-white hover:border-pink-200 shadow-sm'
                                                }`}
                                            >
                                                <span className="text-2xl mb-1.5 block">{style.icon}</span>
                                                <span className={`text-[10px] font-bold uppercase tracking-wider text-center ${
                                                    (pageSettings.openStyle || 'fade') === style.id ? 'text-pink-700' : 'text-gray-500'
                                                }`}>{style.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Animation Preview Box */}
                                    <div className="mt-4 p-6 bg-gray-50/80 rounded-xl flex flex-col items-center justify-center relative border border-gray-200 overflow-hidden h-48 shadow-inner">
                                        <span className="absolute top-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Animation Preview</span>
                                        
                                        <div className="relative w-24 h-32 flex items-center justify-center mt-4">
                                            {/* Fake Invite Card (Underneath) */}
                                            <div className="absolute w-20 h-28 bg-white border border-gray-200 shadow-sm rounded-lg flex flex-col items-center justify-center p-2 z-0">
                                                <div className="w-8 h-8 bg-pink-100 rounded-full mb-2"></div>
                                                <div className="w-12 h-2 bg-gray-100 rounded-full mb-1"></div>
                                                <div className="w-8 h-2 bg-gray-100 rounded-full"></div>
                                            </div>

                                            {/* Fake Envelope (Top) */}
                                            <div 
                                                key={previewKey}
                                                className="absolute inset-0 z-10 flex items-center justify-center"
                                                style={{
                                                    animation: `${pageSettings.openStyle || 'fade'}-preview 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                                                }}
                                            >
                                                <div className="w-24 h-16 bg-pink-500 rounded-md shadow-md flex items-center justify-center border-b-[20px] border-b-pink-600 relative overflow-hidden">
                                                     <div className="absolute top-0 w-0 h-0 border-l-[48px] border-l-transparent border-r-[48px] border-r-transparent border-t-[32px] border-t-pink-400"></div>
                                                     <span className="text-xl relative z-10 mt-[-10px]">💌</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <button 
                                            onClick={() => setPreviewKey(k => k + 1)}
                                            className="absolute bottom-3 right-3 text-[10px] text-pink-600 hover:text-pink-700 font-bold bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-lg border border-pink-100 transition shadow-sm"
                                        >
                                            Replay ↻
                                        </button>

                                        <style>{`
                                            @keyframes fade-preview {
                                                0%, 30% { opacity: 1; transform: scale(1); }
                                                100% { opacity: 0; transform: scale(1.05); }
                                            }
                                            @keyframes slide-up-preview {
                                                0%, 30% { transform: translateY(0); opacity: 1; }
                                                100% { transform: translateY(-150%); opacity: 0; }
                                            }
                                            @keyframes slide-left-preview {
                                                0%, 30% { transform: translateX(0); opacity: 1; }
                                                100% { transform: translateX(-150%); opacity: 0; }
                                            }
                                            @keyframes zoom-in-preview {
                                                0%, 30% { transform: scale(1); opacity: 1; }
                                                100% { transform: scale(2.5); opacity: 0; }
                                            }
                                            @keyframes spin-out-preview {
                                                0%, 30% { transform: rotate(0deg) scale(1); opacity: 1; }
                                                100% { transform: rotate(720deg) scale(0); opacity: 0; }
                                            }
                                        `}</style>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2.5">Custom RSVP Questions</label>
                                    <div className="space-y-2 mb-3">
                                        {pageSettings.customQuestions.map((q, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-white p-3 border border-gray-200 rounded-xl shadow-sm">
                                                <span className="text-sm font-semibold text-gray-800">{q}</span>
                                                <button onClick={() => handleRemoveQuestion(idx)} className="text-gray-400 hover:text-red-500 text-lg font-bold transition">×</button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            placeholder="Ask a question (e.g. Dietary restrictions?)" 
                                            value={newQuestion} 
                                            onChange={(e) => setNewQuestion(e.target.value)} 
                                            className="flex-grow p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                                        />
                                        <button onClick={handleAddQuestion} className="bg-blue-100 text-blue-700 px-5 py-2 rounded-xl text-sm font-bold hover:bg-blue-200 transition">Add</button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2.5">Gift Registry & Links</label>
                                    <div className="space-y-2 mb-3">
                                        {(pageSettings.registryLinks || []).map((link, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-white p-3 border border-gray-200 rounded-xl shadow-sm">
                                                <div className="flex flex-col overflow-hidden mr-2">
                                                    <span className="text-sm font-bold text-gray-800 truncate">{link.title}</span>
                                                    <span className="text-xs text-gray-400 truncate">{link.url}</span>
                                                </div>
                                                <button onClick={() => handleRemoveRegistryLink(idx)} className="text-gray-400 hover:text-red-500 text-lg font-bold transition shrink-0">×</button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input 
                                            type="text" 
                                            placeholder="Store/Title (e.g. Amazon)" 
                                            value={newRegistryTitle} 
                                            onChange={(e) => setNewRegistryTitle(e.target.value)} 
                                            className="p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                                        />
                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="URL (e.g. amazon.com/...)" 
                                                value={newRegistryUrl} 
                                                onChange={(e) => setNewRegistryUrl(e.target.value)} 
                                                className="flex-grow p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                                            />
                                            <button onClick={handleAddRegistryLink} className="bg-green-100 text-green-700 px-5 py-2 rounded-xl text-sm font-bold hover:bg-green-200 transition">Add</button>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Success Message (Going)</label>
                                        <textarea 
                                            placeholder="You're on the list! Yay!" 
                                            value={pageSettings.goingMessage || ''} 
                                            onChange={(e) => setPageSettings({...pageSettings, goingMessage: e.target.value})} 
                                            className="w-full p-3.5 border border-gray-200 rounded-xl text-sm h-24 resize-none bg-white focus:ring-2 focus:ring-pink-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1.5">Decline Message (Not Going)</label>
                                        <textarea 
                                            placeholder="Sorry you can't make it!" 
                                            value={pageSettings.notGoingMessage || ''} 
                                            onChange={(e) => setPageSettings({...pageSettings, notGoingMessage: e.target.value})} 
                                            className="w-full p-3.5 border border-gray-200 rounded-xl text-sm h-24 resize-none bg-white focus:ring-2 focus:ring-pink-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Generation Block */}
                            <div className="pt-8 border-t border-gray-200">
                                {mode === 'ai' ? (
                                    <button onClick={generateContent} disabled={isGenerating} className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-4 rounded-xl font-black text-lg hover:from-purple-700 hover:to-pink-600 transition-all shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none">
                                        {isGenerating ? 'Creating Magic...' : '✨ Generate My E-vite'}
                                    </button>
                                ) : (
                                    <button onClick={generateCustomDesign} disabled={!customImage || isRendering} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-4 rounded-xl font-black text-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none">
                                        {isRendering ? 'Rendering...' : '💾 Finalize Custom Design'}
                                    </button>
                                )}
                                
                                {error && <p className="text-red-500 text-center font-bold mt-4">{error}</p>}
                                
                                {(isGenerating || isRendering) && (
                                    <div className="flex flex-col items-center justify-center py-12 space-y-4 animate-fade-in">
                                        <div className="flex items-center space-x-2 bg-gray-100 px-6 py-4 rounded-full shadow-inner">
                                            <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                        <p className="text-sm font-bold text-gray-500 animate-pulse">
                                            {mode === 'ai' ? 'Host-it! is crafting your masterpiece...' : 'Processing your design...'}
                                        </p>
                                    </div>
                                )}

                                {(generatedImagePreview || generatedText) && !isGenerating && !isRendering && (
                                    <div className="mt-8 pt-8 border-t border-gray-200 animate-fade-in">
                                        <h3 className="text-2xl font-black mb-6 text-center text-gray-900">Your E-vite is Ready!</h3>
                                        <div className="flex flex-col md:flex-row gap-8 items-center bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                            <div className="w-full md:w-1/2">
                                                <img src={generatedImagePreview} alt="Generated party invitation" className="w-full rounded-xl shadow-lg border border-gray-200" />
                                            </div>
                                            {mode === 'ai' && (
                                                <div className="w-full md:w-1/2">
                                                    <textarea value={generatedText} readOnly className="w-full p-4 border border-gray-200 rounded-xl h-64 text-gray-700 bg-white font-medium focus:outline-none resize-none shadow-sm" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="flex flex-col gap-4 mt-6">
                                            {mode === 'ai' ? (
                                                <div className="flex flex-wrap gap-4 justify-between items-center bg-gray-50/50 p-3 rounded-2xl border border-gray-100">
                                                    <div className="flex bg-white rounded-xl p-1 border border-gray-200 shadow-sm">
                                                        <button 
                                                            onClick={handleUndo} 
                                                            disabled={history.length === 0 || history[history.length - 1].mode !== 'ai'}
                                                            className="p-2 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-30 transition" 
                                                            title="Undo"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                                        </button>
                                                        <div className="w-px bg-gray-200 mx-1 my-1"></div>
                                                        <button 
                                                            onClick={handleRedo} 
                                                            disabled={redoStack.length === 0 || redoStack[redoStack.length - 1].mode !== 'ai'}
                                                            className="p-2 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-30 transition" 
                                                            title="Redo"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                                                        </button>
                                                    </div>

                                                    <div className="flex gap-2 flex-grow sm:flex-grow-0 justify-end">
                                                        <button 
                                                            onClick={handleRegenerateImage} 
                                                            disabled={isGenerating}
                                                            className="flex-1 sm:flex-none px-4 py-2.5 bg-white border border-purple-200 text-purple-700 rounded-xl text-xs font-bold hover:bg-purple-50 transition shadow-sm flex items-center justify-center gap-1.5 whitespace-nowrap"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                            {isGenerating ? '...' : 'New Image'}
                                                        </button>
                                                        <button 
                                                            onClick={handleRegenerateText} 
                                                            disabled={isGenerating}
                                                            className="flex-1 sm:flex-none px-4 py-2.5 bg-white border border-blue-200 text-blue-700 rounded-xl text-xs font-bold hover:bg-blue-50 transition shadow-sm flex items-center justify-center gap-1.5 whitespace-nowrap"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                            {isGenerating ? '...' : 'New Text'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button 
                                                    onClick={() => { 
                                                        setGeneratedImagePreview(''); 
                                                        setGeneratedImageBlob(null); 
                                                    }} 
                                                    disabled={isGenerating}
                                                    className="w-full bg-gray-100 text-gray-700 border border-gray-300 py-3 rounded-xl font-bold hover:bg-gray-200 transition shadow-sm flex items-center justify-center gap-2"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" /></svg>
                                                    Edit Design
                                                </button>
                                            )}

                                            <div className="grid grid-cols-2 gap-4 mt-2">
                                                <button onClick={() => setShowPreview(true)} className="py-4 px-4 bg-white border-2 border-gray-200 text-gray-800 rounded-xl font-black shadow-sm hover:bg-gray-50 hover:border-gray-300 transition flex items-center justify-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                    Preview Result
                                                </button>
                                                <button onClick={handleCreateEvent} disabled={loading} className="py-4 px-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-black shadow-lg hover:shadow-xl hover:scale-[1.02] transition transform flex items-center justify-center gap-2 disabled:opacity-75 disabled:scale-100 disabled:cursor-not-allowed">
                                                    {loading ? (
                                                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                    )}
                                                    {loading ? 'Saving...' : (eventId ? 'Save & Continue' : 'Accept & Continue')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Global Wizard Navigation (Only shows inside steps 1-4) */}
                    <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
                        {creationStep > 1 ? (
                            <button onClick={() => setCreationStep(prev => prev - 1)} className="px-6 py-3 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors">← Back</button>
                        ) : <div />}
                        
                        {creationStep < totalCreationSteps ? (
                            <button onClick={() => setCreationStep(prev => prev + 1)} className="px-8 py-3 rounded-xl font-black text-white bg-pink-500 hover:bg-pink-600 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">Next Step →</button>
                        ) : null}
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="fixed inset-0 z-[200] overflow-y-auto bg-gray-100">
                    <RsvpPage 
                        previewData={{
                            ...eventDetails,
                            description,
                            generatedImage: generatedImagePreview,
                            generatedText,
                            pageSettings,
                            hostName: user.plannerName || user.firstName || user.email
                        }} 
                        onClosePreview={() => setShowPreview(false)}
                    />
                </div>
            )}

            {step === 2 && (
                 <div className="bg-white p-4 md:p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
                    <div className="mb-8 pb-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 font-serif tracking-tight mb-2">{eventDetails.title}</h1>
                            <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-gray-500">
                                <span className="flex items-center gap-1.5"><span className="text-lg leading-none mb-0.5">📅</span> {eventDetails.date ? new Date(eventDetails.date).toLocaleDateString() : 'TBD'} at {eventDetails.time || 'TBD'}</span>
                                <span className="hidden sm:inline text-gray-300">•</span>
                                <span className="flex items-center gap-1.5"><span className="text-lg leading-none mb-0.5">📍</span> {eventDetails.location || 'TBD'}</span>
                            </div>
                        </div>
                        <button 
                            onClick={() => setStep(1)} 
                            className="bg-gray-50 text-gray-700 px-5 py-2.5 rounded-xl font-bold hover:bg-gray-100 transition shadow-sm flex items-center gap-2 border border-gray-200 whitespace-nowrap text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit Design
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-center">
                        <div className="bg-emerald-50/50 border border-emerald-100 p-6 rounded-2xl relative overflow-hidden group hover:bg-emerald-50 transition-colors">
                            <p className="text-4xl font-black text-emerald-600 mb-1">{rsvpSummary.attending}</p>
                            <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest">Attending</p>
                        </div>
                        <div className="bg-rose-50/50 border border-rose-100 p-6 rounded-2xl relative overflow-hidden group hover:bg-rose-50 transition-colors">
                            <p className="text-4xl font-black text-rose-600 mb-1">{rsvpSummary.declined}</p>
                            <p className="text-[10px] font-bold text-rose-800 uppercase tracking-widest">Declined</p>
                        </div>
                        <div className="bg-amber-50/50 border border-amber-100 p-6 rounded-2xl relative overflow-hidden group hover:bg-amber-50 transition-colors">
                            <p className="text-4xl font-black text-amber-600 mb-1">{rsvpSummary.pending}</p>
                            <p className="text-[10px] font-bold text-amber-800 uppercase tracking-widest">Pending</p>
                        </div>
                    </div>

                    {}
                    {guests.length > 0 && (
                        <div className="mb-8 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm animate-fade-in">
                            <div className="flex justify-between items-center mb-2">
                                <span className="font-bold text-gray-700 text-sm">Overall Response Rate</span>
                                <span className="font-black text-gray-900">{Math.round(((rsvpSummary.attending + rsvpSummary.declined) / guests.length) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden flex">
                                <div className="bg-emerald-500 h-full transition-all duration-1000" style={{ width: `${(rsvpSummary.attending / guests.length) * 100}%` }}></div>
                                <div className="bg-rose-500 h-full transition-all duration-1000" style={{ width: `${(rsvpSummary.declined / guests.length) * 100}%` }}></div>
                            </div>
                            <p className="text-[10px] text-gray-400 font-medium mt-2 text-center">
                                {rsvpSummary.attending + rsvpSummary.declined} out of {guests.length} guests have responded
                            </p>
                        </div>
                    )}

                    <div className="space-y-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-2">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900">Guest List</h3>
                                <p className="text-sm text-gray-500 font-medium mt-1">Manage invites, track RSVPs, and send reminders.</p>
                            </div>
                            <button 
                                onClick={handleSendInvites} 
                                disabled={sending} 
                                className="bg-pink-600 text-white px-5 py-2.5 rounded-lg font-bold hover:bg-pink-700 transition shadow-sm text-sm disabled:opacity-50 flex items-center gap-2"
                            >
                                {sending ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : <span>💌</span>}
                                {sending ? 'Sending...' : 'Bulk Send Invites'}
                            </button>
                        </div>
                        
                        <div className="bg-gray-50 p-2 rounded-2xl border border-gray-200 shadow-inner flex flex-col md:flex-row gap-2">
                            <input type="text" value={newGuestName} onChange={(e) => setNewGuestName(e.target.value)} placeholder="Guest Name" className="p-3.5 bg-white border border-gray-200 rounded-xl flex-grow text-sm focus:ring-2 focus:ring-pink-500 outline-none transition font-medium" />
                            <input type="email" value={newGuestEmail} onChange={(e) => setNewGuestEmail(e.target.value)} placeholder="Email (Optional)" className="p-3.5 bg-white border border-gray-200 rounded-xl flex-grow text-sm focus:ring-2 focus:ring-pink-500 outline-none transition font-medium" />
                            <PhoneInput value={newGuestPhone} onChange={(e) => setNewGuestPhone(e.target.value)} placeholder="Phone (Optional)" className="p-3.5 bg-white border border-gray-200 rounded-xl w-full md:w-48 text-sm focus:ring-2 focus:ring-pink-500 outline-none transition font-medium" />
                            <button onClick={handleAddGuest} className="bg-gray-900 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-black transition shadow-md whitespace-nowrap text-sm h-[46px] md:h-auto">Add Guest</button>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1 px-2 leading-tight">By adding a phone number, you confirm you have consent to send automated SMS invitations to this guest. Msg & data rates apply. Guests can reply STOP to opt-out.</p>
                        {error && <p className="text-red-500 text-sm font-semibold px-2">{error}</p>}
                        
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden">
                            <div className="p-4 border-b border-gray-100 bg-gray-50/80 flex justify-between items-center">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Name & Contact</span>
                                <span className="bg-white px-2.5 py-1 rounded-md text-[10px] font-bold text-gray-500 border border-gray-200 shadow-sm">{guests.length} Total</span>
                            </div>
                            <div className="divide-y divide-gray-50">
                                {guests.map(guest => (
                                    <div key={guest.id} className="p-4 sm:p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50/50 transition-colors">
                                        <div className="flex items-center gap-4 min-w-0 w-full sm:w-auto">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 flex items-center justify-center text-gray-600 font-bold text-sm shadow-inner flex-shrink-0">
                                                {guest.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-bold text-gray-900 text-base truncate pr-2 leading-tight">{guest.name}</p>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 font-medium mt-1">
                                                    {guest.email && <a href={`mailto:${guest.email}`} className="hover:text-pink-600 transition flex items-center gap-1 truncate" onClick={e=>e.stopPropagation()}><span>✉️</span> <span className="truncate">{guest.email}</span></a>}
                                                    {guest.phone && <a href={`tel:${guest.phone}`} className="hover:text-pink-600 transition flex items-center gap-1" onClick={e=>e.stopPropagation()}><span>📱</span> {guest.phone}</a>}
                                                </div>
                                                {guest.hasPlusOne && (
                                                    <div className="mt-1.5">
                                                        <span className="text-[10px] text-pink-700 font-bold uppercase tracking-wide bg-pink-50 border border-pink-100 px-2 py-0.5 rounded-md inline-block">
                                                            +1: {guest.plusOneName || 'Guest'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto gap-3 border-t border-gray-100 pt-3 sm:border-0 sm:pt-0">
                                            <span className={`px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-widest shadow-sm border ${guest.status === 'Attending' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : guest.status === 'Declined' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                                {guest.status}
                                            </span>
                                            
                                            <div className="flex items-center gap-1.5">
                                                {guest.phone && (
                                                    <a 
                                                        href={`sms:${guest.phone}?body=${encodeURIComponent(`Hi ${guest.name}, ${user.plannerName || user.firstName || 'your friend'} invited you to ${eventDetails.title}. RSVP: ${guest.inviteLink}`)}`}
                                                        className="w-8 h-8 rounded-full bg-white text-gray-600 flex items-center justify-center hover:bg-green-50 hover:text-green-600 transition shadow-sm border border-gray-200 font-medium text-sm"
                                                        title="Text Invite"
                                                    >💬</a>
                                                )}
                                                {guest.email && (
                                                    <a 
                                                        href={`mailto:${guest.email}?subject=${encodeURIComponent(`You're invited to ${eventDetails.title}!`)}&body=${encodeURIComponent(`Hi ${guest.name},\n\nYou are invited to ${eventDetails.title}!\n\nPlease check out the details and let us know if you can make it by viewing your personal invitation here:\n\n${guest.inviteLink}\n\nWe can't wait to celebrate with you!`)}`}
                                                        className="w-8 h-8 rounded-full bg-white text-gray-600 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition shadow-sm border border-gray-200 font-medium text-sm"
                                                        title="Email Invite"
                                                    >✉️</a>
                                                )}
                                                <button 
                                                    onClick={() => handleCopyLink(guest.inviteLink)} 
                                                    className={`px-3 h-8 rounded-full bg-white transition shadow-sm border text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${copiedLink === guest.inviteLink ? 'border-green-200 text-green-700 bg-green-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                                >
                                                    {copiedLink === guest.inviteLink ? '✓ Copied' : '🔗 Copy'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {guests.length === 0 && (
                                    <div className="p-12 text-center text-gray-400 italic font-medium bg-gray-50">
                                        No guests added yet. Add people above to get your custom RSVP links!
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-gray-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">Tools & Distribution</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-between">
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><span>🤝</span> Host Management Link</h4>
                                    <p className="text-xs text-gray-500 mb-4 leading-relaxed">Share this private link with other hosts so they can view and manage the guest list without needing your account login.</p>
                                </div>
                                <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-xl border border-gray-200">
                                    <input type="text" readOnly value={managementLink} className="flex-1 bg-transparent border-none text-xs font-mono text-gray-500 px-2 outline-none w-full" />
                                    <button onClick={() => handleCopyLink(managementLink)} className="bg-white border border-gray-200 shadow-sm text-gray-700 px-4 py-2 rounded-lg text-[10px] font-bold hover:bg-gray-50 transition uppercase tracking-wider whitespace-nowrap">
                                        {copiedLink === managementLink ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            {guests.length > 0 && guests[0].inviteLink && (
                                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-5 group">
                                     <div className="w-20 h-20 bg-gray-50 rounded-xl p-1.5 border border-gray-200 flex-shrink-0 shadow-inner group-hover:border-pink-300 transition-colors">
                                        <img 
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent((window.location?.origin || '') + '/#rsvp/' + user.uid + '_' + eventId + '_new')}`} 
                                            alt="QR Code" 
                                            className="w-full h-full mix-blend-multiply" 
                                        />
                                    </div>
                                    <div className="flex-grow">
                                        <h4 className="font-bold text-gray-900 mb-1">Event QR Code</h4>
                                        <p className="text-xs text-gray-500 leading-relaxed mb-3">Download this QR code to print on physical invitations or display at your event for quick RSVP access.</p>
                                        <a href={`https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent((window.location?.origin || '') + '/#rsvp/' + user.uid + '_' + eventId + '_new')}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-pink-600 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-lg transition shadow-sm inline-block">Download High-Res QR</a>
                                    </div>
                                </div>
                            )}

                            <PhotoQRCode eventId={eventId} eventTitle={eventDetails?.title} />
                            
                            <ThankYouEmailWorker eventId={eventId} eventDetails={eventDetails} setEventDetails={setEventDetails} user={user} db={db} />
                            
                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Automated Thank You Emails</h4>
                                    <p className="text-xs text-gray-500 leading-relaxed max-w-sm">Automatically send a thank you note and photo drop link to attending guests 2 days after the event.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={eventDetails?.autoThankYou !== false}
                                        onChange={async (e) => {
                                            const newValue = e.target.checked;
                                            setEventDetails(prev => ({...prev, autoThankYou: newValue}));
                                            try {
                                                const eventRef = doc(db, `users/${user.uid}/evites/${eventId}`);
                                                await updateDoc(eventRef, { autoThankYou: newValue });
                                            } catch (err) {
                                                console.error("Failed to update setting", err);
                                            }
                                        }}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
                                </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <button
                                onClick={() => {
                                    const eventTitle = eventDetails?.title || 'our event';
                                    const hostName = 'The Host';

                                    const attendingGuests = guests.filter(g => {
                                        const status = (g.status || g.rsvpStatus || '').toLowerCase();
                                        return status === 'attending' || status === 'yes' || g.attended === true;
                                    });
                                    
                                    const emails = attendingGuests.map(g => g.email).filter(Boolean);

                                    if (emails.length === 0) {
                                        console.warn("No attending guests with valid emails found.");
                                        const btn = document.getElementById('thank-you-btn-text');
                                        if (btn) {
                                            const originalText = btn.innerText;
                                            btn.innerText = 'No Emails Found';
                                            setTimeout(() => btn.innerText = originalText, 2000);
                                        }
                                        return;
                                    }

                                    const bcc = emails.join(',');
                                    const subject = encodeURIComponent(`Thank you for attending ${eventTitle}!`);
                                    const body = encodeURIComponent(`Hi everyone,\n\nThank you so much for joining us at ${eventTitle}! We had a wonderful time and hope you did too.\n\nBest,\n${hostName}`);

                                    window.location.href = `mailto:?bcc=${bcc}&subject=${subject}&body=${body}`;
                                }}
                                className="flex items-center justify-center gap-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 py-3 px-4 rounded-xl transition shadow-sm font-semibold text-sm border border-indigo-100"
                                title="Send thank you email to attending guests"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z"/>
                                </svg>
                                <span id="thank-you-btn-text">Thank You Email</span>
                            </button>
                             <button onClick={handleSendReminders} disabled={sending} className="bg-white border border-gray-200 text-gray-800 py-3.5 rounded-xl font-bold shadow-sm hover:bg-gray-50 transition disabled:opacity-50 text-sm flex items-center justify-center gap-2">
                                 <span>🔔</span>
                                 {sending ? 'Processing...' : 'Send Reminders'}
                             </button>
                             <button onClick={() => setShowBroadcastModal(true)} disabled={sending} className="bg-white border border-gray-200 text-gray-800 py-3.5 rounded-xl font-bold shadow-sm hover:bg-gray-50 transition disabled:opacity-50 text-sm flex items-center justify-center gap-2">
                                 <span>📢</span>
                                 Broadcast Message
                             </button>
                        </div>
                    </div>

                    <div className="mt-8 border-t pt-6">
                        <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                            🤖 AI Party Planner
                        </h3>
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                                <div className="text-gray-700">
                                    <p className="font-semibold text-lg text-purple-900">Need help sourcing items?</p>
                                    <p className="text-sm">Our AI can scan your event details and suggest the perfect rentals to match your theme!</p>
                                </div>
                                <button
                                    onClick={() => handleGenerateSuggestions(false)}
                                    disabled={isSuggesting}
                                    className="bg-purple-600 text-white px-6 py-3 rounded-full font-bold hover:bg-purple-700 disabled:opacity-50 transition shadow-md flex items-center gap-2 whitespace-nowrap"
                                >
                                    {isSuggesting ? (
                                        <>Thinking... <span className="animate-spin">✨</span></>
                                    ) : (
                                        <>✨ Suggest Items</>
                                    )}
                                </button>
                            </div>

                            {suggestions.length > 0 && (
                                <div className="mt-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 animate-fade-in">
                                        {suggestions.map((listing) => (
                                            <div key={listing.id} className="relative">
                                                <div className="absolute -top-3 left-0 right-0 z-20 flex justify-center pointer-events-none">
                                                    <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg border-2 border-white text-center max-w-[90%] leading-tight">
                                                        ✨ {listing.suggestionReason}
                                                    </div>
                                                </div>
                                                <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm bg-white">
                                                  <div className="h-48 bg-gray-200">
                                                    <img src={listing.imageUrls?.[0] || 'https://placehold.co/400x300/f2a2e8/ffffff'} alt={listing.title} className="w-full h-full object-cover" />
                                                  </div>
                                                  <div className="p-4">
                                                    <h4 className="font-bold text-gray-900 truncate">{listing.title}</h4>
                                                    <p className="text-pink-600 font-bold mt-1">${listing.price}</p>
                                                    <button onClick={() => navigateTo('listingDetail', listing)} className="mt-3 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 rounded-lg transition">View Item</button>
                                                  </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="text-center mt-10">
                                        <button 
                                            onClick={() => handleGenerateSuggestions(true)}
                                            disabled={isSuggesting}
                                            className="bg-white text-purple-700 border border-purple-200 px-6 py-2 rounded-full font-bold hover:bg-purple-50 transition shadow-sm disabled:opacity-50 flex items-center gap-2 mx-auto"
                                        >
                                            {isSuggesting ? (
                                                <>Finding more... <span className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></span></>
                                            ) : (
                                                <>See More Suggestions +</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {eventDetails.polls?.length > 0 && (
                        <div className="mt-8 border-t pt-6">
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                                📊 Poll Results
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {eventDetails.polls.map(poll => (
                                    <div key={poll.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                        <h4 className="font-bold text-gray-800 mb-3">{poll.question}</h4>
                                        <div className="space-y-3">
                                            {poll.options.map(opt => {
                                                const count = pollResults[poll.id] ? (pollResults[poll.id][opt] || 0) : 0;
                                                const totalVotes = Object.values(pollResults[poll.id] || {}).reduce((a, b) => a + b, 0);
                                                const percent = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                                                
                                                return (
                                                    <div key={opt}>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span className="text-gray-600">{opt}</span>
                                                            <span className="font-bold text-gray-900">{count} ({percent}%)</span>
                                                        </div>
                                                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                            <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${percent}%` }}></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {eventDetails.isPotluck && eventDetails.potluckItems?.length > 0 && (
                        <div className="mt-8 border-t pt-6">
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                                🍲 Potluck Status
                            </h3>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {eventDetails.potluckItems.map(item => (
                                        <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                            <span className={item.assignee ? 'line-through text-gray-400' : 'text-gray-800 font-medium'}>{item.text}</span>
                                            {item.assignee ? (
                                                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                    {item.assignee}
                                                </span>
                                            ) : (
                                                <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                                                    Available
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {pageSettings.allowSongRequests && (
                        <div className="mt-8 border-t pt-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-2xl font-bold flex items-center gap-2">
                                    🎵 DJ Request List
                                </h3>
                                {guests.some(g => g.songRequest || g.songRequestData) && (
                                    <div className="text-right">
                                        <button 
                                            onClick={handleExportPlaylist}
                                            className="text-sm bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full font-bold transition shadow-sm flex items-center gap-2 ml-auto"
                                        >
                                            {playlistCopied ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                                    Copy Playlist
                                                </>
                                            )}
                                        </button>
                                        {playlistCopied && <p className="text-xs text-green-600 font-bold mt-1 transition-opacity duration-500">Ready to paste!</p>}
                                        <p className="text-xs text-gray-500 mt-2 max-w-[220px] leading-tight">
                                            <b>Spotify:</b> Paste (Cmd+V) into Desktop App.<br/>
                                            <b>Apple/YouTube:</b> Use list to search manually.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                {guests.some(g => g.songRequest || g.songRequestData) ? (
                                    <ul className="divide-y divide-gray-100">
                                        {guests.filter(g => g.songRequest || g.songRequestData).map((guest) => {
                                            const song = guest.songRequestData;
                                            return (
                                                <li key={guest.id} className="py-3 flex justify-between items-center">
                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                        <div className="relative group">
                                                            {song ? (
                                                                <img src={song.artworkUrl60} alt="Album Art" className="w-10 h-10 rounded-md bg-gray-200 flex-shrink-0" />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">🎵</div>
                                                            )}
                                                            {song?.previewUrl && (
                                                                <button
                                                                    onClick={() => togglePreview(song.previewUrl)}
                                                                    className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                                                                >
                                                                    <span className="text-white text-xs">{playingPreview === song.previewUrl ? '⏸' : '▶'}</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-gray-900 truncate max-w-[200px] sm:max-w-xs">{song ? song.trackName : guest.songRequest}</p>
                                                            {song && <p className="text-xs text-gray-500 truncate">{song.artistName}</p>}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full whitespace-nowrap ml-2">Requested by {guest.name}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">No songs requested yet.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showBroadcastModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full">
                        <h3 className="text-xl font-bold mb-4">Send Broadcast Message</h3>
                        <p className="text-sm text-gray-600 mb-4">Send an email announcement to all your guests.</p>
                        <div className="space-y-4">
                            <input 
                                type="text" 
                                placeholder="Subject" 
                                value={broadcastSubject} 
                                onChange={(e) => setBroadcastSubject(e.target.value)} 
                                className="w-full p-2 border rounded-md" 
                            />
                            <textarea 
                                placeholder="Message" 
                                value={broadcastMessage} 
                                onChange={(e) => setBroadcastMessage(e.target.value)} 
                                rows="5" 
                                className="w-full p-2 border rounded-md" 
                            />
                        </div>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button onClick={() => setShowBroadcastModal(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition">Cancel</button>
                            <button onClick={handleBroadcast} disabled={sending} className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition">
                                {sending ? 'Sending...' : 'Send Message'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {}
            {toast && (
                <div className={`fixed bottom-6 right-6 z-[500] px-6 py-4 rounded-xl shadow-2xl border animate-fade-in flex items-center gap-3 max-w-sm ${
                    toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                }`}>
                    <span className="text-xl">{toast.type === 'error' ? '⚠️' : '✅'}</span>
                    <span className="font-bold text-sm leading-tight">{toast.message}</span>
                </div>
            )}

            {confirmDialog && (
                <div className="fixed inset-0 z-[600] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-fade-in">
                        <h3 className="text-xl font-black text-gray-900 mb-2">{confirmDialog.title || 'Confirm Action'}</h3>
                        <p className="text-sm font-medium text-gray-600 mb-6 leading-relaxed">{confirmDialog.message}</p>
                        <div className="flex justify-end gap-3">
                            <button 
                                onClick={() => setConfirmDialog(null)} 
                                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold transition shadow-sm text-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDialog.onConfirm} 
                                className="px-5 py-2.5 bg-pink-600 hover:bg-pink-700 text-white rounded-xl font-bold transition shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}